import type { ReleaseNotes } from "../../../src/domain/app-update-contract";
import type { AchievementDict } from "./achievement";
import type { Money } from "./big";
import type { TopBonusImg } from "./match";
import { acceptLanguageOf, loadLang } from "./i18n";
import { clearOwnToken, currentToken, isTicketRejection, ownToken, setOwnToken, tokenExpMs } from "./session";

/** 后端信封 {code, message, data}，code 为字符串，"0" 表示成功 */
interface Envelope<T> { code: string; message: string; data: T }

export class ApiError extends Error {
  /** 信封 data 原样带出：60303 发布校验失败时是 {errors:[{path,rule,actual,expected}]} */
  constructor(message: string, readonly code: string, readonly data?: unknown) {
    super(message);
    this.name = "ApiError";
  }
}

/** 60303 的结构化校验错误行 */
export interface ValidationErrorRow {
  path: string; rule: string; actual: string; expected: string;
  /** "warn" = 只提示不阻断发布；缺省 = 阻断。「还差 N 项」只数阻断项 */
  severity?: "error" | "warn";
}
export function validationErrorsOf(e: unknown): ValidationErrorRow[] | null {
  if (!(e instanceof ApiError)) return null;
  const d = e.data as { errors?: ValidationErrorRow[] } | undefined;
  return Array.isArray(d?.errors) && d.errors.length ? d.errors : null;
}

/**
 * 打管理端 API。
 *
 * 开发时 vite 把 /admin/api 代理到已部署的 test，所以本地和线上用同一套相对路径。
 * token 是从 super-admin 跳转带来的那个，逐请求放 Authorization 头。
 */
/**
 * 当前选中的 A0。用模块级变量而不是每处传参：几乎每个请求都要带它，
 * 逐层透传迟早漏一个，而漏掉的那个会**静默读到默认库**——
 * 数据看着正常，其实是别的租户的。
 */
let currentA0: string | null = null;
export const setApiA0 = (a0: string | null) => { currentA0 = a0; };

/**
 * **透传**给中台的接口——这些用中台 token；其余一律用换来的自有票。
 *
 * 名单方向是刻意的：默认自有票，例外才登记。反过来写（默认中台 token、
 * 自己的接口手工登记）已经出过一次事——`/levels`、`/datasets` 没登记，
 * 被发了中台 token，我们自己的中间件验不了它的签名，**静默 401**，
 * 页面退回 fixture 假数据照常渲染，看不出任何异常。
 *
 * 必须与后端 `src/admin/index.ts` 的 PASS_THROUGH 保持一致：
 * 那边决定「不验我们的票」，这边决定「不发我们的票」，两个名单说的是同一件事。
 */
// `/agents` 2026-09-09 起读本地 A0 注册表、验我们自己的票，不再是透传中台的路径
// `/login` 是账号密码登录：本来就没有票可带，走 loginWithPassword 的裸 fetch；列在这里是为了与后端 PASS_THROUGH 名单一一对应
// `/collab/ws` 是协同编辑的 WebSocket：后端放行后在路由里自己验票（票在子协议里），前端不经 fetch 打它
const UPSTREAM_PATHS = ["/health", "/session/", "/login", "/third/", "/collab/ws"];
const isUpstream = (path: string) => UPSTREAM_PATHS.some((p) => path.startsWith(p));

/**
 * 换票。中台 token → 我们自己能验的短期票。
 * 只在第一次需要时换，换完存起来复用（localStorage，各标签页共用）。
 */
/** 协同编辑房间要的：自有票（放进 WebSocket 子协议）+ 当前代理（放查询串，WS 带不了 X-A0） */
export async function collabConnectInfo(): Promise<{ token: string | null; a0: string | null }> {
  return { token: await ensureOwnToken(), a0: currentA0 };
}

async function ensureOwnToken(): Promise<string | null> {
  const cached = ownToken();
  /*
   * 缓存的票必须先查有效期，不能拿到就用。
   *
   * 踩过：super-admin 的「进入」用 window.open 复用同名窗口，旧标签页
   * 存着的过期自有票被原样带回来——这里直接 return cached，
   * 之后每个请求都 401，页面死在「配置读取失败」，重进多少次都一样。
   * 留 60 秒余量：一张还剩 3 秒的票发出去，到服务端多半已经过期。
   */
  if (cached && tokenExpMs(cached) > Date.now() + 60_000) return cached;
  if (cached) clearOwnToken();
  const up = currentToken();
  if (!up) return null;
  try {
    const res = await fetch("/admin/api/session/exchange", {
      method: "POST", headers: { Authorization: `Bearer ${up}` }, signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
    const body = (await res.json()) as Envelope<{ token: string }>;
    if (body.code !== "0") return null;
    setOwnToken(body.data.token);
    return body.data.token;
  } catch {
    return null;
  }
}

/**
 * 票失效的通知。App 收到后清掉会话、整页换成登录页。
 *
 * 不能只抛错让各页自己显示：页面会停在「配置读取失败：unauthorized」，
 * 重试按钮点多少次都是同一张死票，人只能自己猜要重新登录。
 */
/**
 * 为什么回登录页。原来一律写「登录已过期」，被服务端拒的（票不对、身份缺失）也这么写，
 * 人只会觉得「怎么又过期了」。现在按服务端的码分：10046 才是过期，其余带上服务端原话。
 */
export type AuthLostReason = { kind: "expired" } | { kind: "rejected"; message: string };
let authLostListener: ((reason: AuthLostReason) => void) | null = null;
export const onAuthLost = (fn: ((reason: AuthLostReason) => void) | null) => { authLostListener = fn; };

/**
 * 401 统一出口：票的问题就清票、通知回登录页；不是票的问题照常抛给页面。
 *
 * `sent` 是这次请求带的票。票是各标签页共用的，清票等于所有标签页一起退出——
 * 所以只在存储里还是被拒的那张时才清：请求在路上的这段时间别的标签页续了票、重新登录了，
 * 被拒的只是旧票，不能把新票也清掉。
 */
async function rejectUnauthorized(res: Response, ownTicket: boolean, sent: string | null): Promise<never> {
  const body = (await res.json().catch(() => null)) as { code?: unknown; message?: unknown } | null;
  if (!isTicketRejection(body)) throw new ApiError(String(body?.message), String(body?.code ?? "401"));
  const live = ownTicket ? ownToken() : currentToken();
  if (sent && live && live !== sent) throw new ApiError("unauthorized", "401");
  // 服务端说这张票不行，就别留着它——不清的话刷新页面还是同一张死票、同一个 401
  if (ownTicket) clearOwnToken();
  authLostListener?.(String(body?.code) === "10046" || typeof body?.message !== "string"
    ? { kind: "expired" }
    : { kind: "rejected", message: body.message });
  throw new ApiError("unauthorized", "401");
}


/**
 * 读写接口的超时。浏览器的 fetch 没有默认超时：连接半路断了、回包一直不来，await 就永远挂着。
 * 实时保存一轮里任何一个请求挂住，整轮就停在「保存中」，之后的自动保存全被跳过，页面却照常能改、能传图——
 * 10-28 1201 上 kaori 保存后的读回请求没到服务端，之后 11 分钟传的 36 张关卡图只活在浏览器里，刷新就没了。
 * 超时按网络错误抛（不是 ApiError）：实时保存走「写没写进去不知道」的对账重试，不会当成被拒。
 * 发布要整版校验，给足一分钟；上传另算（大文件）
 */
export const REQUEST_TIMEOUT_MS = 60_000;

async function get<T>(path: string, params?: Record<string, string>): Promise<T> {
  const a0 = currentA0;
  const qs = params ? `?${new URLSearchParams(params)}` : "";
  const token = isUpstream(path) ? currentToken() : await ensureOwnToken();
  // 语言要一路传到中台：它按 language 头决定返回内容的语言。
  // 不传的话切了英文界面，代理名之类仍是中文，界面一半中一半英。
  const lang = acceptLanguageOf(loadLang());
  const res = await fetch(`/admin/api${path}${qs}`, {
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    headers: {
      "Accept-Language": lang,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(a0 ? { "X-A0": a0 } : {}),
    },
  });
  if (res.status === 401) await rejectUnauthorized(res, !isUpstream(path), token);
  const body = (await res.json()) as Envelope<T>;
  if (body.code === "60001" || body.code === "401") {
    throw new ApiError(body.message, body.code);
  }
  if (body.code !== "0") throw new ApiError(body.message, body.code);
  return body.data;
}

export interface Agent {
  a0: string;
  name: string;
  /**
   * 这个代理有没有**自己的**数值配置。
   * false 不代表不能用——它读全局默认那份，照常能用；只是运营改的是共用值。
   */
  hasOwnConfig: boolean;
  /** 1 启用 / 0 停用（停用的代理登录会被拒，顶栏默认不列出） */
  status?: number;
  /** IANA 时区名，管端展示用 */
  tz?: string;
  note?: string;
  /** manual = 界面/迁移写入；upstream = 从中台导入 */
  source?: string;
  updatedAtMs?: number;
  /** 历史字段：中台透传时代的降级标记，现已不再产生 */
  degraded?: boolean;
  auth_bridge_app_id: string | null;
  auth_bridge_secret_set: boolean;
  auth_bridge_secret_updated_at_ms: number | null;
  auth_bridge_secret_updated_by: string | null;
  /** 代理管理页的 Ably 推送开关。游戏端缓存 60 秒、只管新加入 */
  ably_enabled?: boolean;
  /** 配置文件 ABLY_A0S 已开了这个代理（过渡期兜底）：游戏端两边取并集，在管端关不掉 */
  ably_env_enabled?: boolean;
  /** 客户端埋点开关：开了这个代理的客户端才初始化数数 SDK。默认关、只有超管能开；游戏端缓存 60 秒 */
  client_analytics_enabled?: boolean;
  /** 服务端埋点开关：开了且服务端配置 TA_EVENTS_A0S 含这个代理，服务端事件才上报。默认关、只有超管能开；游戏端缓存 60 秒 */
  server_analytics_enabled?: boolean;
  /**
   * 内购商店凭证（按平台，只写不读）：只有「填没填」与 keyId / client_email 这类标识，私钥不回传。
   * 没填的平台用服务端配置的默认凭证。后端没上线时没有这些字段，按「未设置」算
   */
  iap_ios_set?: boolean;
  iap_ios_issuer_id?: string | null;
  iap_ios_key_id?: string | null;
  iap_ios_updated_at_ms?: number | null;
  iap_ios_updated_by?: string | null;
  iap_android_set?: boolean;
  iap_android_client_email?: string | null;
  iap_android_rtdn_audience?: string | null;
  iap_android_updated_at_ms?: number | null;
  iap_android_updated_by?: string | null;
  /** App 版本更新策略（按平台）。后端没上线时没有这个字段，按「未设」算 */
  app_update?: {
    ios: AgentAppUpdatePlatform;
    android: AgentAppUpdatePlatform;
    updated_at_ms: number | null;
    updated_by: string | null;
  };
  /** 配置变更通知的飞书群 webhook 填没填（地址只写不读） */
  notify_webhook_set?: boolean;
  notify_webhook_updated_at_ms?: number | null;
  notify_webhook_updated_by?: string | null;
}

export interface AgentAppUpdatePlatform {
  release_notes?: ReleaseNotes | null;
  min_version: string | null; latest_version: string | null; store_url: string | null;
  /** 最低代码版本：代码版本（热更新后的版本）低于它要先热更新。后端没上线时没有这个字段，按「未设」算 */
  min_code_version?: string | null;
}

export const fetchAgents = (keyword = "") => get<Agent[]>("/agents", keyword ? { keyword } : undefined);
/** 代理管理页：含停用的全部代理 */
export const fetchAllAgents = (keyword = "") => get<Agent[]>("/agents", { all: "1", ...(keyword ? { keyword } : {}) });
/** ably_enabled / client_analytics_enabled / server_analytics_enabled 不传 = 不改；只有超管能开 */
export const upsertAgent = (a0: string, body: { name: string; status: number; tz: string; note: string; ably_enabled?: boolean; client_analytics_enabled?: boolean; server_analytics_enabled?: boolean }) =>
  send<Agent>("PUT", `/agents/${encodeURIComponent(a0)}`, body);
export interface AgentAuthBridgeInput { app_id: string; secret?: string; clear_secret?: boolean }
export const putAgentAuthBridge = (a0: string, input: AgentAuthBridgeInput) => send<Agent>("PUT", `/agents/${encodeURIComponent(a0)}/auth-bridge`, input);
/** 配置变更通知：{webhook} 替换，{clear: true} 删掉 */
export const putAgentNotify = (a0: string, input: { webhook: string } | { clear: true }) => send<Agent>("PUT", `/agents/${encodeURIComponent(a0)}/notify`, input);
/** 往群里发一条测试卡片，结果当场交回 */
export const testAgentNotify = (a0: string) => send<{ ok: boolean; msg: string }>("POST", `/agents/${encodeURIComponent(a0)}/notify/test`, {});
export interface AgentIapIos { issuerId: string; keyId: string; privateKey: string }
/** service account JSON 原样传也行（服务端只留 client_email / private_key / private_key_id），rtdn 可选 */
export interface AgentIapAndroid {
  client_email: string; private_key: string; private_key_id?: string;
  rtdn?: { audience: string; serviceAccountEmail: string } | null;
  [k: string]: unknown;
}
/** 每个平台：不传 = 保留；null = 清除（回到默认凭证）；对象 = 整块替换 */
export interface AgentIapInput { ios?: AgentIapIos | null; android?: AgentIapAndroid | null }
export const putAgentIap = (a0: string, input: AgentIapInput) => send<Agent>("PUT", `/agents/${encodeURIComponent(a0)}/iap`, input);
/** 版本更新策略：每个平台整块替换，不传 = 保留；空串 / null = 不设那一项 */
export interface AgentAppUpdateInput { ios?: AgentAppUpdatePlatform; android?: AgentAppUpdatePlatform }
export const putAgentAppUpdate = (a0: string, input: AgentAppUpdateInput) => send<Agent>("PUT", `/agents/${encodeURIComponent(a0)}/app-update`, input);
/** 头像库 / 昵称库 / 国家字典（从 portwind-avatar 同步到本地，游客建号时按国家随机取默认头像与昵称） */
export type NameLibraryKind = "countries" | "avatars" | "matches" | "nicknames";
export interface NameLibraryStats { countries: number; avatars: number; matches: number; nicknames: number }
export interface NameLibraryProgress { kind: NameLibraryKind; total: number; synced: number; nextOffset: number | null; pages: number }
export const fetchNameLibraryStats = () => get<NameLibraryStats>("/name-library/stats");
/** 跑最多 pages 页，返回 nextOffset；为 null 表示这一类同步完了。调用方循环直到 null */
export const syncNameLibrary = (kind: NameLibraryKind, offset: number, pages = 10) =>
  send<NameLibraryProgress>("POST", `/name-library/sync?kind=${kind}&offset=${offset}&pages=${pages}`);

export interface AuditEntry {
  id: number; a0: string; actor: string; action: string;
  target: string; detail: Record<string, unknown> | null; ip: string; atMs: number;
  /** 操作落在哪个配置版本上；与版本无关为 null，老服务端没有这个字段 */
  configVersion?: number | null;
}
export interface AuditPage { total: number; page: number; page_size: number; items: AuditEntry[] }

export type ConfigUndoSelection = { sourceVersion?: number; actor?: string; ids?: number[]; field?: { block: string; path: string }; blocks?: string[] };
export type ConfigUndoItem = { key: string; change: import("./autosave").Change; state: "ready" | "conflict" | "already" | "unavailable"; reason?: "later" | "different" | "missing" | "incomplete" | "legacy" | "schema" | "identity"; sourceIds: number[] };
export type ConfigUndoPreview = { revision: number; fingerprint: string; items: ConfigUndoItem[]; applied: number; sourceVersion?: number };
export type { ConfigModuleRestore } from "../../../src/domain/config-modules";
export const previewModuleRestoreApi = (version: number, selection: import("../../../src/domain/config-modules").ConfigModuleRestore) => get<ConfigUndoPreview>(`/config/versions/${version}/module-restore`, selection);
export const applyModuleRestoreApi = (version: number, selection: import("../../../src/domain/config-modules").ConfigModuleRestore, preview: ConfigUndoPreview) => send<ConfigUndoPreview>("POST", `/config/versions/${version}/module-restore`, { selection, expected_revision: preview.revision, fingerprint: preview.fingerprint });
export const previewConfigUndoApi = (version: number, selection: ConfigUndoSelection) => get<ConfigUndoPreview>(`/config/versions/${version}/undo`, { selection: JSON.stringify(selection) });
export const applyConfigUndoApi = (version: number, selection: ConfigUndoSelection, preview: ConfigUndoPreview) => send<ConfigUndoPreview>("POST", `/config/versions/${version}/undo`, { selection, expected_revision: preview.revision, fingerprint: preview.fingerprint });

export const fetchAudit = (p: { page?: number; pageSize?: number; actor?: string; action?: string; a0?: string; version?: number; target?: string; configOnly?: boolean } = {}) =>
  get<AuditPage>("/audit", {
    page: String(p.page ?? 1),
    ...(p.pageSize ? { page_size: String(p.pageSize) } : {}),
    ...(p.actor ? { actor: p.actor } : {}),
    ...(p.action ? { action: p.action } : {}),
    ...(p.a0 ? { a0: p.a0 } : {}),
    // 只看某一版上发生的事：「谁建的这个草稿、谁改过什么」
    ...(p.version != null ? { version: String(p.version) } : {}),
    // 只看某一条对象：精确匹配 target（自动留痕的请求路径）
    ...(p.target ? { target: p.target } : {}),
    // 只看落在配置版本上的记录：按人跨版本看「他改了哪些配置」
    ...(p.configOnly ? { config: "1" } : {}),
  });

/**
 * 记一条操作日志。**故意不 await、不抛错** ——
 * 日志失败不该让用户的动作跟着失败，也不该弹错误提示打断他。
 */
/**
 * 记一条操作日志。**故意不 await、不抛错** ——
 * 日志失败不该让用户的动作跟着失败，也不该弹错误提示打断他。
 */
export function logAction(action: string, target?: string, detail?: unknown, a0?: string): void {
  void ensureOwnToken()
    .then((token) => {
      if (!token) return;
      return fetch("/admin/api/audit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          ...(a0 ? { "X-A0": a0 } : {}),
        },
        body: JSON.stringify({ action, target, detail, a0 }),
      });
    })
    .catch(() => {});
}


/** 带 body 的写请求。信封与错误处理与 get 同一套 */
async function send<T>(method: "POST" | "PUT" | "PATCH" | "DELETE", path: string, body?: unknown, extraHeaders: Record<string, string> = {}): Promise<T> {
  // Bind the request before token exchange yields: changing the tenant selector
  // must not redirect a write already started by the previous tenant's editor.
  const a0 = currentA0;
  const token = await ensureOwnToken();
  const res = await fetch(`/admin/api${path}`, {
    method,
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(a0 ? { "X-A0": a0 } : {}),
      ...extraHeaders,
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (res.status === 401) await rejectUnauthorized(res, true, token);
  const envelope = (await res.json()) as Envelope<T>;
  if (envelope.code !== "0") throw new ApiError(envelope.message, envelope.code, envelope.data);
  return envelope.data;
}

/**
 * 上传美术资源，返回对象键与完整 CDN URL（页面存 url，不存 key）。
 * module 决定 CDN 目录 `aeroplane/<module>/…`，服务端不传默认 card；
 * 页面别直接调这个，走 components/data/useAssetUpload。
 */
export async function uploadAsset(
  file: File, module?: string, name?: string,
): Promise<{ key: string; url: string; duration_sec?: number }> {
  const a0 = currentA0;
  const token = await ensureOwnToken();
  // name 带上就改按后缀受理（Spine 的 .skel / .atlas / .meta 没有 MIME，只能按文件名判），
  // 并把原名保留在对象键里。页面别自己拼这个参数，走 useAssetUpload 的 byName
  const qs = [
    module ? `module=${encodeURIComponent(module)}` : "",
    name ? `name=${encodeURIComponent(name)}` : "",
  ].filter(Boolean).join("&");
  const withThumbs = !name && ["image/png", "image/jpeg", "image/webp"].includes(file.type);
  const uploadBody = withThumbs ? await (await import("./static-thumbnails")).thumbnailUploadBody(file) : file;
  const res = await fetch(`/admin/api/assets${qs ? `?${qs}` : ""}`, {
    method: "POST",
    signal: AbortSignal.timeout(120_000),
    headers: {
      // 浏览器认不出 .skel/.atlas/.meta 时 file.type 是空串，服务端会补 octet-stream
      ...(!withThumbs ? { "Content-Type": file.type } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(a0 ? { "X-A0": a0 } : {}),
    },
    body: uploadBody,
  });
  if (res.status === 401) await rejectUnauthorized(res, true, token);
  // duration_sec 只有 VIP 视频位（module=idol-vip-video）回：服务端从 mp4 文件头读出来的时长
  const envelope = (await res.json()) as Envelope<{ key: string; url: string; duration_sec?: number }>;
  if (envelope.code !== "0") throw new ApiError(envelope.message, envelope.code);
  return envelope.data;
}

/* ── 热更新发布（对应 src/admin/hot-update.ts） ── */

export type HotPlatform = "ios" | "android";
export type HotKind = "full" | "diff";
export type HotStatus = "draft" | "internal" | "live" | "withdrawn";
export interface HotUpdateRow {
  id: string; platform: HotPlatform; kind: HotKind; codeVersion: string; minNativeVersion: string; maxNativeVersion: string | null;
  url: string; sha256: string; sizeBytes: number; status: HotStatus; note: string;
  createdAtMs: number; createdBy: string; publishedAtMs: number | null; publishedBy: string | null;
  withdrawnAtMs: number | null; withdrawnBy: string | null;
}
export interface HotUpdateList { rows: HotUpdateRow[]; min_code_version: Record<HotPlatform, string | null> }
export interface HotUploadInput { platform: HotPlatform; kind: HotKind; code_version: string; min_native_version: string; max_native_version?: string; note?: string }

export const fetchHotUpdates = () => get<HotUpdateList>("/hot-updates");
export const publishHotUpdateApi = (id: string) => send<{ row: HotUpdateRow; shadowed_by: string[] }>("POST", `/hot-updates/${encodeURIComponent(id)}/publish`, {});
export const publishInternalHotUpdateApi = (id: string) => send<{ row: HotUpdateRow; shadowed_by: string[] }>("POST", `/hot-updates/${encodeURIComponent(id)}/publish-internal`, {});
export interface InternalDeviceRow { deviceId: string; note: string; createdBy: string; createdAtMs: number }
export const fetchInternalDevices = () => get<{ devices: InternalDeviceRow[] }>("/hot-updates/devices");
export const addInternalDeviceApi = (deviceId: string, note: string) => send<InternalDeviceRow>("POST", "/hot-updates/devices", { device_id: deviceId, note });
export const removeInternalDeviceApi = (deviceId: string) => send<{ deleted: boolean }>("DELETE", `/hot-updates/devices?device_id=${encodeURIComponent(deviceId)}`);
export const withdrawHotUpdateApi = (id: string) => send<HotUpdateRow>("POST", `/hot-updates/${encodeURIComponent(id)}/withdraw`, {});
export const deleteHotUpdateApi = (id: string) => send<{ deleted: boolean }>("DELETE", `/hot-updates/${encodeURIComponent(id)}`);
export const setHotMinCodeApi = (platform: HotPlatform, codeVersion: string | null) =>
  send<{ before: string | null; after: string | null }>("PUT", "/hot-updates/min-code-version", { platform, code_version: codeVersion });

/**
 * 上传热更新 zip 并登记为草稿。用 XHR 是为了上传进度（fetch 拿不到）；信封与 401 处理同 send。
 * 服务端会核对 zip、version.json 与自己算 sha256，回登记好的行
 */
export async function uploadHotUpdateApi(file: File, meta: HotUploadInput, onProgress: (pct: number) => void): Promise<HotUpdateRow> {
  const a0 = currentA0;
  const token = await ensureOwnToken();
  const qs = new URLSearchParams(Object.entries(meta).filter(([, v]) => v != null && v !== "") as Array<[string, string]>);
  const { status, text } = await new Promise<{ status: number; text: string }>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `/admin/api/hot-updates?${qs}`);
    xhr.setRequestHeader("Content-Type", "application/zip");
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    if (a0) xhr.setRequestHeader("X-A0", a0);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(Math.min(99, Math.floor((e.loaded / e.total) * 100))); };
    xhr.onload = () => resolve({ status: xhr.status, text: xhr.responseText });
    xhr.onerror = () => reject(new ApiError("网络错误，上传没有完成", "network"));
    xhr.send(file);
  });
  if (status === 401) await rejectUnauthorized(new Response(text, { status }), true, token);
  const envelope = JSON.parse(text) as Envelope<{ row: HotUpdateRow; shadowed_by: string[] }>;
  if (envelope.code !== "0") throw new ApiError(envelope.message, envelope.code, envelope.data);
  onProgress(100);
  return envelope.data.row;
}

/* ── 配置版本链（对应 src/admin/config.ts） ── */

export interface VersionInfo {
  configVersion: number;
  /** draft / published（后端没有独立的 active 状态，生效与否看 active 标志） */
  status: string;
  /** 写请求的 CAS 修订号 */
  revision: number;
  checksum: string;
  note: string | null;
  author: string;
  createdAtMs: number;
  rollbackFrom: number | null;
  /** 草稿的底子（拷自的已发布版本）；底子不是线上生效版本时，发布会先把线上的改动合进来 */
  baseVersion?: number | null;
  active: boolean;
  /** 最后一次写入内容的人与时刻（服务端在写接口成功后盖的）；从没写过时为空串 / null */
  updatedBy?: string;
  updatedAtMs?: number | null;
  /** 这一版最近一次设为生效时的生效方式；从没生效过 / 后端没上线为 null 或没有 */
  lastRefreshMode?: string | null;
  /** 金额口径（docs/economy-g-model.md §2.4）：1 = B 数 × 有效 B，2 = 单掷金币 g 与「几骰」系数。老服务端没有 = 1 */
  economyModel?: 1 | 2;
  /** model 2 的公共刻度 num/den（十进制串）；model 1 为 null */
  moneyScaleNum?: string | null;
  moneyScaleDen?: string | null;
  /** 迁移前身的 E（千分位）；没有旧口径前身为 null */
  legacyEMilli?: number | null;
  /** 发布人与时刻；草稿、没留下发布日志的旧版本为空串 / null */
  publishedBy?: string;
  publishedAtMs?: number | null;
  /** 在这一版上动过手的人，按最后一次操作倒序 */
  contributors?: Array<{ actor: string; count: number; lastAtMs: number }>;
}

export const fetchVersions = () => get<VersionInfo[]>("/config/versions");
/**
 * 建草稿。**一个代理同时只有一个草稿**：已经有了就把它交回来（`reused: true`），
 * 不新建也不报错。`revision` 是那个草稿此刻的修订号，调用方必须拿它做 CAS——
 * 复用的草稿早被改过几轮，自己假定 0 的话第一条补丁就撞冲突。
 */
export interface DraftHandle {
  configVersion: number;
  revision: number;
  reused: boolean;
  /** 复用时才有：这个草稿是谁建的、什么时候建的 */
  author?: string;
  createdAtMs?: number;
}
/** rollback：「回滚」入口传 true——新草稿的底子记此刻线上的版本，发布时不会把线上的改动合回去 */
export const createDraftApi = (from: number, note?: string, rollback = false) =>
  send<DraftHandle>("POST", "/config/versions", { from, note, ...(rollback ? { rollback: true } : {}) });
/**
 * 初始化配置那两个单选此刻能不能改。**代理级事实**（线上生效那一版 + 有没有玩家），
 * 不带版本号；判据与发布闸同源，界面只负责如实反映。
 */
export interface InitLocks {
  gameMode: { locked: boolean; active: string };
  buildingSystem: { locked: boolean; active: string };
}
export const fetchInitLocks = () => get<InitLocks>("/config/init-locks");
/** 清掉多余草稿，只留最新那个（「一个代理一个草稿」定下之前攒的存量） */
export const pruneDraftsApi = () =>
  send<{ kept: number | null; deleted: number[]; skipped: Array<{ version: number; reason: string }> }>(
    "POST", "/config/drafts/prune", {});
/** 从 0 建这个代理的第一版（空版本）。新代理无处可拷——配置都属于某个真实代理 */
export const createFirstDraftApi = (note?: string) =>
  send<DraftHandle>("POST", "/config/versions", { from: null, note });
/** 以另一个代理的生效版本为底稿建这个代理的第一版（超管；目标代理必须一版都还没有） */
export const createFirstFromAgentApi = (fromA0: string) =>
  send<DraftHandle & { from: { a0: string; configVersion: number } }>("POST", "/config/versions", { from_a0: fromA0 });
/** 发布时把线上改动合进草稿的结果（admin/config-merge.ts）：草稿原来拷自 from，合到了线上的 onto，改了 rows 处 */
export interface PublishMerge { from: number; onto: number; rows: number; tables: Record<string, number> }
/** keepDraft：合并冲突时选了「保留草稿的值」。activeAtPublish：发布那一刻的线上版本，接着自动设为生效时带上 */
/** note：随发布写进版本备注；不传就保留草稿原有的备注 */
export const publishVersionApi = (v: number, expectedRevision: number, keepDraftOnto?: number, note?: string) =>
  send<{ revision: number; checksum: string; merged?: PublishMerge | null; activeAtPublish?: number | null }>(
    "POST", `/config/versions/${v}/publish`, {
      expected_revision: expectedRevision,
      ...(keepDraftOnto !== undefined ? { keep_draft_onto: keepDraftOnto } : {}),
      ...(note !== undefined ? { note } : {}),
    });
/** 发布时合并有冲突（409）：信封 data 带冲突清单，管端据此给「保留草稿的值并发布」 */
export function mergeConflictsOf(e: unknown): { total: number; levelsMoved: boolean; onto: number } | null {
  if (!(e instanceof ApiError)) return null;
  const d = e.data as { merge_conflicts?: unknown[]; total?: number; levels_moved?: boolean; onto?: number } | undefined;
  return Array.isArray(d?.merge_conflicts) && Number.isInteger(d?.onto)
    ? { total: Number(d!.total ?? d!.merge_conflicts.length), levelsMoved: d!.levels_moved === true, onto: Number(d!.onto) }
    : null;
}
/** spin 盘面。整盘替换，与副玩法格同款：一整盘是一份完整定义 */
export interface SpinStatsResp {
  totalRolls: number;
  cells: Array<{ dropType: string; hits: number; coinSum: string }>;
  fromMs: number; toMs: number; levels: number[] | null;
}
/** spin 落点报表：真实 roll 的按格命中计数（只读） */
export const fetchSpinStats = (fromMs: number, toMs: number, levels: number[] | null) =>
  get<SpinStatsResp>("/stats/spin", {
    from_ms: String(fromMs), to_ms: String(toMs),
    ...(levels && levels.length ? { levels: levels.join(",") } : {}),
  });

/**
 * 玩家面板（只读）。三个接口都在 /players 下：总览、分页列表、单人详情。
 * 金额一律走字符串——金币/钻石是 bigint，转 number 会在 2^53 上失真。
 */
export interface PlayerRow {
  userIdHex: string;
  nickname: string | null;
  /** 玩家在游戏里自己改的昵称（列表与详情都带）；显示用 shownName */
  customNickname?: string | null;
  avatar: string | null;
  cityLevel: number;
  clearedLevels: number;
  levelRolls: number;
  levelPaidCoins: string;
  totalRolls: number;
  totalInvested: string;
  coins: string;
  gems: string;
  dice: number;
  freeRolls: number;
  shields: number;
  wildcard: number;
  puzzleFrag: number;
  cards: number;
  packsDone: number;
  idols: number;
  idolQty: number;
  stealGain: string;
  stealLoss: string;
  thunderHit: number;
  shieldBlock: number;
  daily: Record<string, number>;
  orders: number;
  bids: number;
  payer: boolean;
  profileAtMs: number;
  lang: string | null;
  userStatus: number | null;
  lastLoginAtMs: number;
  lastLoginIp: string | null;
  userType: number | null;
  lastActiveMs: number;
  createdAtMs: number;
}
export interface PlayerOverviewResp {
  total: number; activeToday: number; newToday: number; avgLevel: number; maxLevel: number;
  rollsToday: number; coins: string; gems: string; dice: string; payers: number; orders: number; cards: number;
  levelBuckets: Array<{ bucket: number; count: number }>; nowMs: number;
}
export interface PlayerListResp { rows: PlayerRow[]; total: number; page: number; size: number; nowMs: number }
export interface PlayerDetailResp {
  player: PlayerRow;
  assets: Array<{ assetId: string; amount: string }>;
  cityLogs: Array<{ level: number; nRolls: number; invested: string; completedAtMs: number }>;
  attacks: Array<{ kind: string; role: string; amount: string; buildingIdx: number | null; createdAtMs: number }>;
  daily: Array<{ counterType: string; count: number }>;
  buildings: Array<{ buildingIdx: number; level: number; damaged: boolean }>;
  cards: Array<{ packId: string; cardCode: string; qty: number }>;
  packs: string[];
  idols: Array<{ idolCardCode: string; source: string; qty: number; obtainedAtMs: number }>;
  /** 宠物持有（user_pet）。老版服务端没有这个键，读的地方按空数组处理 */
  pets?: Array<{ petId: string; qty: number; star: number; lineupOrder: number | null }>;
  /** 拼图提示次数（今天 UTC）。老版服务端没有这个键 */
  puzzleHint?: PuzzleHint;
  /** 每位 idol 的亲密度与关卡图拼完数（2026-10-23）。老版服务端没有这个键 */
  intimacy?: Array<{ idolId: string; stageName: string; exp: number; level: number; todayGained: number; stagesAssembled: number; updatedAtMs: number | null }>;
  nowMs: number;
}
/** 拼图提示次数：上限、今天已用、剩余、下次自动恢复的时刻 */
export interface PuzzleHint { limit: number; used: number; remaining: number; resets_at_ms: number }
export interface PlayerQuery {
  kw?: string; levelMin?: number | null; levelMax?: number | null;
  pay?: "all" | "payer" | "free"; sort?: string; page?: number; size?: number;
}

export const fetchPlayerOverview = () => get<PlayerOverviewResp>("/players/overview");

export const fetchPlayers = (q: PlayerQuery) =>
  get<PlayerListResp>("/players", {
    ...(q.kw ? { kw: q.kw } : {}),
    ...(q.levelMin ? { level_min: String(q.levelMin) } : {}),
    ...(q.levelMax ? { level_max: String(q.levelMax) } : {}),
    ...(q.pay && q.pay !== "all" ? { pay: q.pay } : {}),
    ...(q.sort ? { sort: q.sort } : {}),
    page: String(q.page ?? 1),
    size: String(q.size ?? 20),
  });

export const fetchPlayerDetail = (userIdHex: string) => get<PlayerDetailResp>(`/players/${userIdHex}`);
/** 把这名玩家今天已用的拼图提示次数清零 */
export const resetPlayerPuzzleHint = (userIdHex: string) =>
  send<{ usedBefore: number; puzzleHint: PuzzleHint }>("POST", `/players/${userIdHex}/puzzle-hint/reset`, {});

// ── 充值记录（src/admin/recharge-orders.ts）：现金内购单 pay_order，只读 ──
export type PayStatus = "pending" | "paying" | "success" | "failed" | "expired" | "refunding" | "refunded";
export const PAY_STATUSES: readonly PayStatus[] = ["pending", "paying", "success", "failed", "expired", "refunding", "refunded"];
export interface RechargeQuery {
  userIdHex?: string; orderId?: string; thirdOrderId?: string; statuses?: PayStatus[];
  platform?: "ios" | "android" | ""; env?: "production" | "sandbox" | ""; itemId?: string;
  fromMs?: number; toMs?: number; cursor?: string | null; size?: number;
}
export interface RechargeRow {
  orderId: string; userIdHex: string; nickname: string; avatar: string;
  itemId: string; itemName: string; itemNameI18n: Record<string, string>; itemDeleted: boolean;
  kind: "once" | "subscription"; platform: string; environment: string; status: PayStatus;
  /** amount 是十进制字符串；null = 商店没回报实付金额 */
  currency: string; amount: string | null; thirdOrderId: string; createdAtMs: number; updatedAtMs: number;
}
export interface RechargePage { rows: RechargeRow[]; next_cursor: string | null; has_more: boolean }
export interface RechargeSummary {
  orders: number; credited: number; creditedAmounts: Array<{ currency: string; amount: string }>;
  refunded: number; refundedAmounts: Array<{ currency: string; amount: string }>; payers: number;
}
export interface RechargeAssetQty { asset_id: string; qty: number | string }
export interface RechargeDetail extends RechargeRow {
  productId: string; err: string; deliveredOpId: string; refundRequestedAtMs: number | null;
  delivery: { contents: RechargeAssetQty[]; firstBonus: RechargeAssetQty[]; durationDays: number };
  deliver: { granted: RechargeAssetQty[]; firstBonusGranted: RechargeAssetQty[] } | null;
  refund: { reclaimed: Array<{ asset_id: string; granted: string; deducted: string; debt: string }>; debt: RechargeAssetQty[] } | null;
  refundDebt: RechargeAssetQty[];
  verify: { transactionId: string; originalTransactionId: string; environment: string; purchaseDate: string } | null;
  /**
   * 交易回原账号：另一个会话交上来的交易认领到这张单上。原账号自己验的为 null。
   * 跨代理时普通管理员拿到的 a0 / userId 为 null（只显示「由其他代理的会话补验」），超管才有完整值
   */
  recoveredBy: { sameAgent: boolean; a0: string | null; userId: string | null } | null;
}
const rechargeParams = (q: RechargeQuery): Record<string, string> => ({
  ...(q.userIdHex ? { user_id_hex: q.userIdHex } : {}),
  ...(q.orderId ? { order_id: q.orderId } : {}),
  ...(q.thirdOrderId ? { third_order_id: q.thirdOrderId } : {}),
  ...(q.statuses?.length ? { status: q.statuses.join(",") } : {}),
  ...(q.platform ? { platform: q.platform } : {}),
  ...(q.env ? { env: q.env } : {}),
  ...(q.itemId ? { item_id: q.itemId } : {}),
  ...(q.fromMs != null ? { from_ms: String(q.fromMs) } : {}),
  ...(q.toMs != null ? { to_ms: String(q.toMs) } : {}),
});
export const fetchRechargeOrders = (q: RechargeQuery) =>
  get<RechargePage>("/recharge-orders", {
    ...rechargeParams(q), ...(q.cursor ? { cursor: q.cursor } : {}), size: String(q.size ?? 50),
  });
export const fetchRechargeSummary = (q: RechargeQuery) => get<RechargeSummary>("/recharge-orders/summary", rechargeParams(q));
/** 打开详情服务端会记一条审计（action=recharge.view） */
export const fetchRechargeOrder = (orderId: string) => get<RechargeDetail>(`/recharge-orders/${encodeURIComponent(orderId)}`);

export const fetchSpinOf = (version: number) =>
  get<{ configVersion: number; revision: number; status: string; cells: import("./board").ServerSpinCell[] }>(
    `/config/versions/${version}/spin`);
export const putSpinApi = (v: number, expectedRevision: number, cells: unknown[]) =>
  send<{ revision: number }>("PUT", `/config/versions/${v}/spin`,
    { expected_revision: expectedRevision, replace_all: true, cells });

/**
 * 副玩法格。读侧走 /config/tiles（按 X-A0 + ?version 定位），写侧逐格 PUT。
 *
 * 补这两个函数之前，spin 与 6 个副玩法格页跑的是 buildTiles 的本地假数据：
 * 页面渲染正常、不报错、也没有降级横幅，改完保存不报错，刷新才发现没存。
 */
export const fetchTilesOf = (version: number) =>
  get<import("./server-tiles").ServerTilesPayload>("/config/tiles", { version: String(version) });
export const putTileApi = (v: number, expectedRevision: number, tileId: string, tile: unknown) =>
  send<{ revision: number }>("PUT", `/config/versions/${v}/tiles/${tileId}`,
    { expected_revision: expectedRevision, tile });

/** 设为生效。refreshMode 是这次的生效方式；force 只有超管能选（服务端把关） */
/** expectedActive：发布完自动设为生效时带上发布那一刻的线上版本，中间被别人换过服务端就拒绝。手点「设为生效」不带 */
export const activateVersionApi = (v: number, refreshMode: import("./refresh-mode").RefreshMode = "normal", expectedActive?: number | null) =>
  send<unknown>("POST", `/config/versions/${v}/activate`, { refresh_mode: refreshMode, ...(expectedActive !== undefined ? { expected_active: expectedActive } : {}) });
export const unfreezeVersionApi = (v: number, expectedRevision: number) =>
  send<unknown>("POST", `/config/versions/${v}/unfreeze`, { expected_revision: expectedRevision });

/** 逐字段关卡补丁（对应后端 LevelPatch）。改 nBuildings/nLevels 必须连全量 cells 一起提交 */
export interface LevelPatchBody {
  bValue?: Money;
  targetRolls?: Money;
  targetRollsOverride?: Money | null;
  costMatrixSeed?: number | null;
  rollsRangePct?: number;
  /** 显式 null = 清空回退算法总造价 */
  actualC?: Money | null;
  /** 单掷金币 g（千分之一金币的十进制串，只有 model 2 版本收）；显式 null = 清空回曲线 */
  gMilli?: string | null;
  /** 关卡封面图 URL；空串 = 清空 */
  coverImg?: string;
  /** 建造页底图 URL；空串 = 清空 */
  bgImg?: string;
  starShapeBps?: number[];
  bldInit?: (Money | null)[];
  nameI18n?: Record<string, string>;
  stageI18n?: Record<string, string>;
  theme?: string;
  nBuildings?: number;
  nLevels?: number;
  cells?: Array<{ buildingIdx: number; starLevel: number; cost: Money }>;
  /** 本次 patch 里有人标 main 时后端先整关清零再写，保证一关一栋 */
  buildings?: Array<{ idx: number; name: string; nameI18n?: Record<string, string>; main?: boolean; offsetX?: number; offsetY?: number }>;
  /** 关卡资源三槽位整对象替换；{} = 清空回全默认（服务端存 NULL） */
  levelAssets?: Record<string, { img?: string; from?: number }>;
}

export const patchLevelApi = (v: number, level: number, expectedRevision: number, patch: LevelPatchBody) =>
  send<{ revision: number }>("PATCH", `/config/versions/${v}/levels/${level}`, { expected_revision: expectedRevision, patch });

/** 新建关卡（插到 at 位，其后关卡整体顺延）。bValue/targetRolls 缺省 = 待填 */
export interface NewLevelBody {
  /** 稳定身份；服务端用它挡掉回包丢失后的重复建关 */
  uid?: string;
  nameI18n: Record<string, string>;
  stageI18n?: Record<string, string>;
  bValue?: Money;
  targetRolls?: Money;
  targetRollsOverride?: Money | null;
  /** 单掷金币 g（千分之一金币的十进制串，只有 model 2 版本收） */
  gMilli?: string;
  costMatrixSeed?: number | null;
  rollsRangePct?: number;
  nBuildings: number;
  nLevels: number;
  /** 长度必须 = nLevels */
  starShapeBps: number[];
  bldInit?: (Money | null)[];
  buildings?: Array<{ idx: number; name: string; main?: boolean; offsetX?: number; offsetY?: number }>;
  cells?: Array<{ buildingIdx: number; starLevel: number; cost: Money }>;
}
export const createLevelApi = (v: number, expectedRevision: number, at: number, level: NewLevelBody) =>
  send<{ revision: number }>("POST", `/config/versions/${v}/levels`, { expected_revision: expectedRevision, at, level });
/** 拖动改编号：第 from 关整关挪到第 to 关，中间顺移。unfixable = 搬完没法固定下来的图（搬之前是空、搬完继承到了图） */
export const moveLevelApi = (v: number, expectedRevision: number, from: number, to: number) =>
  send<{ revision: number; unfixable: Array<{ level: number; field: string; buildingSet: string }> }>("POST", `/config/versions/${v}/levels/move`, { expected_revision: expectedRevision, from, to });
/** 删除第 level 关，后面各关号前移一位 */
export const deleteLevelApi = (v: number, expectedRevision: number, level: number) =>
  send<{ revision: number; unfixable: Array<{ level: number; field: string; buildingSet: string }> }>("DELETE", `/config/versions/${v}/levels/${level}`, { expected_revision: expectedRevision });
/** 各关当前人数（管端，不缓存） */
export const fetchLevelPlayers = () => get<{ levels: Array<{ level: number; players: number }>; total: number }>("/players/levels");

/* ── 全局参数 / 奖励 / 卡包 / 竞拍（均随 config_version，一套 revision CAS） ── */

/** cfg_main_common 一行，全部是数字列（bps/milli/sec 原样，换算在 server-config.ts） */
/** 金币量级的列（incomeAnchorB、petMaxedCoin）是 DECIMAL，读出是十进制字符串 */
export type ServerCommon = Record<string, number> & { incomeAnchorB?: Money; petMaxedCoin?: Money };
export const fetchCommonOf = (v: number) =>
  get<{
    configVersion: number; revision: number; status: string; common: ServerCommon | null;
    /** 建筑方案选择器。字符串列，不在数值 common 里 */
    buildingSet: string;
    /** 玩法体系选择器。空串 = 存量未选，两边都放行（见服务端 game/game-mode.ts） */
    gameMode: string;
    /** 建筑体系：levels | pettown | ""（还没选）。空串是真实状态，界面要显示成「未选择」 */
    buildingSystem: string;
    buildingPresentation?: string;
    /** 客户端用哪套造价：formula / manual / rival:<id>；空串 = 还没选（发布闸拦） */
    costSource?: string;
    costCurve?: string;
    /** 最高奖励四种奖励的图；服务端还没部署这一列时没有这个字段 */
    topBonusImg?: TopBonusImg;
  }>(`/config/versions/${v}/common`);
/** 只跑校验、不动状态。返回的 issues 与发布被拒时那份同源同形 */
/** 接口视角：这一版现场生成的资源块（只读、不落库），附生效版同一块 */
export interface ApiLensChunk { key: string; hash: string; bytes: number; body: unknown }
export const fetchApiLens = (v: number, keys: string[], at?: number) =>
  get<{ configVersion: number; status: string; revision: number; activeVersion: number | null;
    chunks: Record<string, { draft: ApiLensChunk | null; live: ApiLensChunk | null }> }>(
    `/config/versions/${v}/api-lens`, { keys: keys.join(","), ...(at != null ? { at: String(at) } : {}) });

export const checkVersionApi = (v: number) =>
  get<{ configVersion: number; issues: Array<{ path: string; rule: string; actual: unknown; expected: string; severity?: "error" | "warn" }> }>(
    `/config/versions/${v}/check`);

export const patchCommonApi = (v: number, expectedRevision: number, patch: Record<string, number | string>) =>
  send<{ revision: number }>("PATCH", `/config/versions/${v}/common`, { expected_revision: expectedRevision, patch });
/**
 * 三个字符串选择器走同一个 PATCH，但不进数值 patch——服务端也是分开的两条路。
 * 建筑体系（levels / pettown）与前两个同类：都是「开服前要定、定了影响整套配置」的开关。
 */
export const patchSelectorsApi = (
  v: number, expectedRevision: number,
  sel: { gameMode?: string; buildingSet?: string; buildingSystem?: string; buildingPresentation?: string; costSource?: string; costCurve?: string; topBonusImg?: TopBonusImg },
) =>
  send<{ revision: number }>("PATCH", `/config/versions/${v}/common`, {
    expected_revision: expectedRevision,
    ...(sel.gameMode === undefined ? {} : { game_mode: sel.gameMode }),
    ...(sel.buildingSet === undefined ? {} : { building_set: sel.buildingSet }),
    ...(sel.buildingSystem === undefined ? {} : { building_system: sel.buildingSystem }),
    ...(sel.buildingPresentation === undefined ? {} : { building_presentation: sel.buildingPresentation }),
    ...(sel.costSource === undefined ? {} : { cost_source: sel.costSource }),
    ...(sel.costCurve === undefined ? {} : { cost_curve: sel.costCurve }),
    ...(sel.topBonusImg === undefined ? {} : { top_bonus_img: sel.topBonusImg }),
  });

// ── 竞品列（随版本存）：列定义整表替换 / 关卡制逐格价按关卡范围替换 / 萌宠小镇逐星价整列替换 ──
export interface ServerRival { rivalId: string; name: string; sortOrder: number }
export interface RivalsPayload {
  configVersion: number; revision: number; status: string;
  rivals: ServerRival[];
  /** 关卡制逐格价，稀疏 */
  cells: Array<{ rivalId: string; level: number; buildingIdx: number; starLevel: number; cost: Money }>;
  /** 萌宠小镇逐星价，稀疏 */
  pettownPrices: Array<{ rivalId: string; star: number; price: Money }>;
}
export const fetchRivalsOf = (v: number) => get<RivalsPayload>(`/config/versions/${v}/rivals`);

/**
 * 按玩家指定造价来源（leo 2026-09-17）：版本默认来源对所有人生效，这里指定的玩家单独用另一套价，
 * 一个版本里同时测竞品和公式。不跟版本走（不在版本快照里），改完 30 秒内生效。
 */
export interface PlayerCostSourceRow {
  userIdHex: string; nickname: string | null; costSource: string; note: string; updatedAtMs: number;
  /** 指定的竞品列不在生效版本里：运行时按版本默认取价，界面标红 */
  missingInActive: boolean;
  /** 正在建的这颗星已填了几格（萌宠小镇）：换来源后剩下的格按新价扣 */
  stepInStar: number;
}
export interface PlayerCostSourcePayload {
  rows: PlayerCostSourceRow[];
  activeVersion: number | null;
  defaultSource: string;
  activeRivals: Array<{ rivalId: string; name: string }>;
  max: number;
}
export const fetchPlayerCostSources = () => get<PlayerCostSourcePayload>("/players/cost-sources");
export const putPlayerCostSources = (userIdHexes: string[], costSource: string, note: string) =>
  send<PlayerCostSourcePayload>("PUT", "/players/cost-sources", { user_id_hexes: userIdHexes, cost_source: costSource, note });
export const fetchPlayerCostSource = (userIdHex: string) =>
  get<{ row: PlayerCostSourceRow | null } & Omit<PlayerCostSourcePayload, "rows">>(`/players/${userIdHex}/cost-source`);
export const deletePlayerCostSource = (userIdHex: string) => send<PlayerCostSourcePayload>("DELETE", `/players/${userIdHex}/cost-source`);
/** 列定义整表替换：顺序即数组顺序；没带 rivalId 的行由服务端发号。删掉的列连它的价一起删 */
export const putRivalsApi = (v: number, expectedRevision: number, rivals: Array<{ rivalId?: string; name: string }>) =>
  send<{ revision: number; rivals: ServerRival[] }>("PUT", `/config/versions/${v}/rivals`,
    { expected_revision: expectedRevision, replace_all: true, rivals });
/** 某一列在若干关的逐格价：levels 声明本次覆盖哪些关（同 bulkPutCells） */
export const putRivalCellsApi = (
  v: number, expectedRevision: number, rivalId: string, levels: number[],
  cells: Array<{ level: number; buildingIdx: number; starLevel: number; cost: Money }>,
) =>
  send<{ revision: number; written: number }>("PUT", `/config/versions/${v}/rivals/${rivalId}/cells`,
    { expected_revision: expectedRevision, replace_all: true, levels, cells });
/** 某一列的萌宠小镇逐星价整列替换 */
export const putPettownRivalPricesApi = (v: number, expectedRevision: number, rivalId: string, prices: Array<{ star: number; price: Money }>) =>
  send<{ revision: number; written: number }>("PUT", `/config/versions/${v}/rivals/${rivalId}/pettown-prices`,
    { expected_revision: expectedRevision, replace_all: true, prices });

// ── 萌宠小镇：星级表 + 副建筑槽位（整表替换，理由同社交两表） ──
/**
 * 星级表是**稀疏覆盖**：只有改过什么的星才有行（目标骰次 / 手填价 / 格数 / 外观图 / B 值），
 * 页面按「累计星数上限」铺满。bValue 原样带回去，服务端没收到也会保留现有值
 */
export const fetchPettownStarsOf = (v: number) =>
  get<{ configVersion: number; revision: number; status: string; stars: import("./pettown").PettownStar[] }>(
    `/config/versions/${v}/pettown/stars`);
export const putPettownStarsApi = (v: number, expectedRevision: number, stars: import("./pettown").PettownStar[]) =>
  send<{ revision: number }>("PUT", `/config/versions/${v}/pettown/stars`,
    { expected_revision: expectedRevision, replace_all: true, stars });
export const fetchPettownSlotsOf = (v: number) =>
  get<{ configVersion: number; revision: number; status: string; slots: import("./pettown").PettownSlot[] }>(
    `/config/versions/${v}/pettown/slots`);
export const putPettownSlotsApi = (v: number, expectedRevision: number, slots: import("./pettown").PettownSlot[]) =>
  send<{ revision: number }>("PUT", `/config/versions/${v}/pettown/slots`,
    { expected_revision: expectedRevision, slots });

export interface ServerReward {
  rewardId: string;
  buildingSystem?: "levels" | "pettown";
  level: number;
  name: string;
  nameI18n: Record<string, string> | null;
  items: Array<{ kind: string; ref?: string | null; qty: number | null }>;
}
export const fetchRewardsOf = (v: number) =>
  get<{ configVersion: number; revision: number; status: string; rewards: ServerReward[] }>(`/config/versions/${v}/rewards`);
export const putRewardApi = (v: number, expectedRevision: number, reward: Omit<ServerReward, "name">) =>
  send<{ revision: number }>("PUT", `/config/versions/${v}/rewards/${reward.rewardId}`, { expected_revision: expectedRevision, reward });
export const deleteRewardApi = (v: number, expectedRevision: number, rewardId: string) =>
  send<{ revision: number }>("DELETE", `/config/versions/${v}/rewards/${rewardId}`, { expected_revision: expectedRevision });

/* ── 任务 / 成就（cfg_achievement）。V1.0 改造 ── */
export interface ServerAchievementStage {
  stage: number;
  target: number | null;
  items: Array<{ kind: string; ref?: string | null; qty: number | null; q?: string | null; sn?: string | null; sd?: string | null }>;
  by_level?: Array<{ min_level: number | null; items: Array<{ kind: string; ref?: string | null; qty: number | null; q?: string | null; sn?: string | null; sd?: string | null }> }> | null;
  by_star?: Array<{ min_star: number | null; items: Array<{ kind: string; ref?: string | null; qty: number | null; q?: string | null; sn?: string | null; sd?: string | null }> }> | null;
  label_i18n?: Record<string, string> | null;
  desc_i18n?: Record<string, string> | null;
}
export interface ServerAchievement {
  achievementId: string;
  period: string;
  kind: string;
  window: string;
  metric: string;
  /** 活跃点：**独立字段不是奖励项** */
  points: number;
  weight: number;
  mustShow: boolean;
  replaceWhen: { cond: string; with: string } | null;
  newbieDay: number | null;
  hidden: boolean;
  params: Record<string, unknown> | null;
  title: string;
  titleI18n: Record<string, string>;
  descI18n: Record<string, string>;
  icon: string;
  jumpTo: string;
  category: string;
  stages: ServerAchievementStage[];
  sortOrder: number;
  enabled: boolean;
}
/**
 * **不走配置版本**（用户 2026-09-07）：没有 :v、没有 expected_revision，保存即生效。
 * 校验在服务端写入时做，失败 422 / 60303 带 errors（validationErrorsOf 能取）。
 * 成功时可能带 warnings（如「这个指标依赖的玩法还没上线」）——**提醒不拦**。
 */
/**
 * 任务成就分玩法（2026-10-26）：同一代理下 Idol Ludo（ludo）与二合（erhe）各一套任务 / 成就 / 新手七日 / 模块参数 / 称号目录，
 * 互不可见。下面每个任务成就接口都带 `?game=`；服务端缺省 ludo、未知值 400。指标字典（/achievement-metrics）两套共用，不带。
 */
export type AchievementGame = "ludo" | "erhe";
const gameQs = (game: AchievementGame) => `?game=${encodeURIComponent(game)}`;
export const fetchAchievements = (game: AchievementGame = "ludo") => get<{ achievements: ServerAchievement[] }>("/config/achievements", { game });
/** create：页面上没有这个编号（新建）。服务端已有就拒，不按编号覆盖别人刚建的 */
export const putAchievementApi = (achievement: Omit<ServerAchievement, "title">, create = false, game: AchievementGame = "ludo") =>
  send<{ ok: true; warnings?: Array<{ path: string; rule: string; actual: string; expected: string }> }>(
    "PUT", `/config/achievements/${achievement.achievementId}${gameQs(game)}`, { achievement, create });
export const deleteAchievementApi = (achievementId: string, game: AchievementGame = "ludo") =>
  send<{ ok: true }>("DELETE", `/config/achievements/${achievementId}${gameQs(game)}`, {});

/**
 * 新手七日整天保存：一天的子任务 + 收口大奖一次写完。
 * 不能拆成几个单条 PUT——一天的几行互相约束（大奖目标值 = 启用子任务数），
 * 分几次发中间会不一致，任一次失败就把这一天写成半截。
 */
export const putAchievementDayApi = (day: number, rows: Array<Omit<ServerAchievement, "title">>, deleteIds: string[], createIds: string[] = [], game: AchievementGame = "ludo") =>
  send<{ ok: true; warnings?: Array<{ path: string; rule: string; actual: string; expected: string }> }>(
    "PUT", `/config/achievements-day${gameQs(game)}`, { day, rows, deleteIds, createIds });

/** 模块级参数：每天抽几条、新手期几天。同样不走配置版本 */
export const fetchAchievementSettings = (game: AchievementGame = "ludo") =>
  get<{ dailyTaskCount: number; newbieDays: number; newbieDurationDays: number }>("/config/achievement-settings", { game });
export const putAchievementSettingsApi = (v: Partial<{ dailyTaskCount: number; newbieDays: number; newbieDurationDays: number }>, game: AchievementGame = "ludo") =>
  send<{ dailyTaskCount: number; newbieDays: number; newbieDurationDays: number }>("PUT", `/config/achievement-settings${gameQs(game)}`,
    { daily_task_count: v.dailyTaskCount, newbie_days: v.newbieDays, newbie_duration_days: v.newbieDurationDays });

/** 称号（cfg_title）：成就最高档发的东西 */
export interface ServerTitle {
  titleId: string;
  name: string;
  nameI18n: Record<string, string>;
  icon: string;
  sortOrder: number;
  enabled: boolean;
}
/** 称号目录按玩法分开列；编号跨两套全局唯一（服务端新建时校验） */
export const fetchTitles = (game: AchievementGame = "ludo") => get<{ titles: ServerTitle[] }>("/config/titles", { game });
export const putTitleApi = (title: Omit<ServerTitle, "name">, create = false, game: AchievementGame = "ludo") =>
  send<{ ok: true }>("PUT", `/config/titles/${title.titleId}${gameQs(game)}`, { title, create });
export const deleteTitleApi = (titleId: string, game: AchievementGame = "ludo") =>
  send<{ ok: true }>("DELETE", `/config/titles/${titleId}${gameQs(game)}`, {});

/** 活动道具名词表（cfg_act_item）：奖励项 ref → 名字 / 图标 / 说明 */
export interface ServerActItem {
  ref: string;
  name: string;
  nameI18n: Record<string, string>;
  descI18n: Record<string, string>;
  icon: string;
  sortOrder: number;
  enabled: boolean;
}
export const fetchActItems = () => get<{ items: ServerActItem[] }>("/config/act-items");
export const putActItemApi = (item: Omit<ServerActItem, "name">, create = false) =>
  send<{ ok: true }>("PUT", `/config/act-items/${item.ref}`, { item, create });
export const deleteActItemApi = (ref: string) =>
  send<{ ok: true }>("DELETE", `/config/act-items/${ref}`, {});

/**
 * 活动道具收集玩法的排期与档位（cfg_act_event / cfg_act_event_tier）。
 *
 * **不带 expectedRevision**：这两张表不进配置版本，保存即生效（同商城 / idol / 称号），
 * 没有草稿 revision 可以 CAS。`status` 与 `cumulative` 是服务端按当前时刻算好带回来的，
 * 只读——客户端另算一遍就会在跨零点、时钟漂移时和服务端说的不一样。
 */
export interface ServerActEvent {
  eventId: string;
  nameI18n: Record<string, string>;
  actItemRef: string;
  startAtMs: number | null;
  endAtMs: number | null;
  skinId: string;
  noticeI18n: Record<string, string>;
  fullBehavior: "fallback" | "keep";
  tiers: Array<{ tier: number; need: number; rewards: Array<{ kind: string; ref?: string | null; qty: number | null; q?: string | null; sn?: string | null; sd?: string | null }>; icon?: string }>;
  status: "draft" | "scheduled" | "running" | "ended";
  cumulative: number[];
  createdAtMs: number;
}
/** economyModel：当前生效版本的金额口径，金币奖励编辑的是哪一份（1 = B 数，2 = 几骰系数） */
export const fetchActEvents = () => get<{ events: ServerActEvent[]; economyModel?: 1 | 2 }>("/config/act-events");
/** 已有的一期：id 在路径里。新建用 createActEventApi，id 由服务端分配 */
export const putActEventApi = (event: Omit<ServerActEvent, "status" | "cumulative" | "createdAtMs">) =>
  send<{ ok: true; eventId: string }>("PUT", `/config/act-events/${event.eventId}`, { event });
export const createActEventApi = (event: Omit<ServerActEvent, "status" | "cumulative" | "createdAtMs" | "eventId">) =>
  send<{ ok: true; eventId: string }>("POST", "/config/act-events", { event });
/** 手动开关（2026-10-06）：起止时刻只由这两个动作写，保存不再带时间 */
export const openActEventApi = (eventId: string) =>
  send<{ ok: true }>("POST", `/config/act-events/${eventId}/open`, {});
export const closeActEventApi = (eventId: string) =>
  send<{ ok: true }>("POST", `/config/act-events/${eventId}/close`, {});
export const deleteActEventApi = (eventId: string) =>
  send<{ ok: true }>("DELETE", `/config/act-events/${eventId}`, {});

/** 存钱罐（活动配置，保存即生效；服务端 admin/piggy.ts）。iOS / Android 各从内购配置里选一件内购商品，两个都不选 = 免费敲开 */
export interface PiggyIn {
  piggyId: string;
  nameI18n: Record<string, string>;
  enabled: boolean;
  assetId: string;
  /** 存钱罐容量，金币量级没有上限（DECIMAL，读出是十进制字符串） */
  cap: Money;
  rewards: Array<{ asset_id: string; qty: number }>;
  /** 敲开奖励里的宠物 / 物品卡 / 称号 */
  extras: Array<{ kind: "pet" | "itemCard" | "title"; ref: string; qty: number }>;
  startMode: "time" | "level";
  startAtMs: number | null;
  endAtMs: number | null;
  unlockLevel: number;
  durationHours: number;
  /** iOS 玩家敲开要买的内购商品 item_id；空串 = iOS 没配 */
  itemIdIos: string;
  /** Android 玩家敲开要买的内购商品 item_id；空串 = Android 没配。两个都空 = 免费敲开 */
  itemIdAndroid: string;
}
export interface PiggyRow extends PiggyIn {
  /** 两个平台各自那件商品的价格；没配 / 商品已删为 null */
  cashPriceIos: { currency: string; amount: number } | null;
  cashPriceAndroid: { currency: string; amount: number } | null;
  /** iOS 那件的 App Store 商品 ID、Android 那件的 Google Play 商品 ID；空 = 没配或没填 */
  iapProductIos: string;
  iapProductAndroid: string;
  createdAtMs: number;
  updatedAtMs: number;
}
/** 能选的内购商品（内购配置里的现金商品），每个平台只列填了那个平台商品 ID 的；usedBy = 已被哪一期选走（哪个平台都算），空串 = 没被选 */
export interface PiggyIapItem {
  itemId: string; name: string; nameI18n: Record<string, string>; status: string;
  cashPrice: { currency: string; amount: number } | null; iapProductIos: string; iapProductAndroid: string; usedBy: string;
}
export interface PiggyOptions { pets: Array<{ id: string; name: string }>; titles: Array<{ id: string; name: string }>; itemPacks: Array<{ id: string; name: string }>; iapItems: PiggyIapItem[] }
export const fetchPiggies = () => get<{ piggies: PiggyRow[]; options: PiggyOptions }>("/config/piggies");
export const createPiggyApi = (piggy: PiggyIn) => send<{ piggies: PiggyRow[] }>("POST", "/config/piggies", { piggy });
export const updatePiggyApi = (piggy: PiggyIn) => send<{ piggies: PiggyRow[] }>("PUT", `/config/piggies/${encodeURIComponent(piggy.piggyId)}`, { piggy });
export const deletePiggyApi = (piggyId: string) => send<{ piggies: PiggyRow[] }>("DELETE", `/config/piggies/${encodeURIComponent(piggyId)}`, {});

/** 付费链（活动配置，保存即生效；服务端 admin/pay-chain.ts，禅道 9009）。格子按顺序解锁：免费格点领取，付费格走内购 */
export interface PayChainReward { kind: string; ref?: string; qty: number | string }
export interface PayChainCell { type: "free" | "paid"; rewards: PayChainReward[]; itemIdIos: string; itemIdAndroid: string }
export interface PayChainIn { chainId: string; nameI18n: Record<string, string>; enabled: boolean; startAtMs: number; endAtMs: number; cells: PayChainCell[] }
export interface PayChainRow extends PayChainIn {
  /** 已经有玩家领过 / 买过：格子的数量、类型、内购商品不能再改 */
  started: boolean; createdAtMs: number; updatedAtMs: number;
}
export interface PayChainIapItem {
  itemId: string; name: string; nameI18n: Record<string, string>; status: string;
  cashPrice: { currency: string; amount: number } | null; iapProductIos: string; iapProductAndroid: string;
  /** 已被谁选走：`chain_id#格` 或 `piggy:<id>`；空串 = 没被选 */
  usedBy: string;
}
export interface PayChainOptions { pets: Array<{ id: string; name: string }>; titles: Array<{ id: string; name: string }>; itemPacks: Array<{ id: string; name: string }>; iapItems: PayChainIapItem[] }
export const fetchPayChains = () => get<{ chains: PayChainRow[]; options: PayChainOptions }>("/config/pay-chains");
export const createPayChainApi = (chain: PayChainIn) => send<{ chains: PayChainRow[] }>("POST", "/config/pay-chains", { chain });
export const updatePayChainApi = (chain: PayChainIn) => send<{ chains: PayChainRow[] }>("PUT", `/config/pay-chains/${encodeURIComponent(chain.chainId)}`, { chain });
export const deletePayChainApi = (chainId: string) => send<{ chains: PayChainRow[] }>("DELETE", `/config/pay-chains/${encodeURIComponent(chainId)}`, {});

/**
 * 配置页下拉字典：指标 / 周期 / 类型 / 窗口 / 跳转 / 探测器 / 条件替换 / 奖励类型，
 * 全部来自服务端代码常量。**分类是例外**——V1.0 起是自由字符串，这里给的只是候选。
 */
export const fetchAchievementMetrics = () => get<AchievementDict>("/achievement-metrics");

export interface ServerPack {
  packId: string;
  kind: "item" | "idol";
  code: string;
  name: string;
  nameI18n: Record<string, string> | null;
  cover: boolean;
  /** 封面图 R2 key（空串 = 未传，迁移 0040） */
  coverImg?: string;
  disabled: boolean;
  setReward: string | null;
  /** 集齐奖励（结构化，cfg_card_pack.set_rewards，迁移 0045）；null = 未配 */
  setRewards?: Array<{ kind: string; ref?: string | null; qty: number | null }> | null;
  /** idol 包：商城一次抽几张（cfg_card_pack.shop_draw_count，迁移 0047） */
  shopDrawCount?: number | null;
  /** idol 包：商城价格（钻石，cfg_card_pack.shop_price_gem，迁移 0048） */
  shopPriceGem?: number | null;
  unlockPack: string | null;
  /** idol 包：所属 idol（cfg_card_pack.idol_id，迁移 0059）；空串 / null = 未绑定 */
  idolId?: string | null;
  /** 目标张数；null = 未定（迁移 0027 起落库） */
  count: number | null;
  /** 可被万能卡兑换（cfg_card_pack.wildcard_ok，迁移 0036） */
  wildcardOk: boolean;
  /** 出包权重（cfg_card_pack.weight）；null = 待填 */
  weight: number | null;
  /** 掉落关卡 [{level, rateBps}]（cfg_card_pack.drops JSON）；null/[] = 不掉落 */
  drops: Array<{ level: number; rateBps: number }> | null;
  starDrops?: Array<{ star: number; rateBps: number }> | null;
  /** 卡牌明细随包整读整写，原样 JSON */
  cards: unknown[];
}
export const fetchPacksOf = (v: number) =>
  get<{ configVersion: number; revision: number; status: string; packs: ServerPack[] }>(`/config/versions/${v}/card-packs`);
export const putPackApi = (v: number, expectedRevision: number, pack: Omit<ServerPack, "name">) =>
  send<{ revision: number }>("PUT", `/config/versions/${v}/card-packs/${pack.packId}`, { expected_revision: expectedRevision, pack });
/** 物品掉落配置：按玩家等级整级替换（空 entries = 该级不掉）。只写 drops，putPackApi 已不写它 */
export const putPackDropsApi = (v: number, expectedRevision: number, levels: Array<{ level: number; entries: Array<{ packId: string; rateBps: number }> }>) =>
  send<{ revision: number }>("PUT", `/config/versions/${v}/card-pack-drops`, {
    expected_revision: expectedRevision,
    levels: levels.map((l) => ({ level: l.level, entries: l.entries.map((e) => ({ pack_id: e.packId, rate_bps: e.rateBps })) })),
  });
export const deletePackApi = (v: number, expectedRevision: number, packId: string) =>
  send<{ revision: number }>("DELETE", `/config/versions/${v}/card-packs/${packId}`, { expected_revision: expectedRevision });

export interface ServerPeriod {
  seq: number;
  bidStartMs: number | null;
  bidEndMs: number | null;
  minIncBps: number | null;
  sparkBonusBps: number | null;
  enabled: boolean;
}
export const fetchAuctionsOf = (v: number) =>
  get<{ configVersion: number; revision: number; status: string; byLevel: Record<string, ServerPeriod[]> }>(`/config/versions/${v}/auctions`);
export const putAuctionLevelApi = (v: number, expectedRevision: number, level: number, periods: Omit<ServerPeriod, "seq">[]) =>
  send<{ revision: number }>("PUT", `/config/versions/${v}/auctions/${level}`, { expected_revision: expectedRevision, replace_all: true, periods });
/** 一次 HTTP 给全部关卡追加最新一期 */
export const newAuctionPeriodApi = (v: number, expectedRevision: number, period: Omit<ServerPeriod, "seq" | "enabled">) =>
  send<{ revision: number }>("POST", `/config/versions/${v}/auctions/new-period`, { expected_revision: expectedRevision, period });

/* ── 写真拼图（随 config_version，一套 revision CAS） ── */

export const fetchPuzzlesOf = (v: number) =>
  get<{ configVersion: number; revision: number; status: string; puzzles: import("./puzzle").ServerPuzzle[] }>(
    `/config/versions/${v}/puzzles`);
/**
 * 新建拼图：**带全部内容一次写入**，ID 由服务端分配后回传。
 *
 * 不是「先建空行、再 PUT 补内容」——那两步非原子，第二步失败就在库里留下一行
 * 图空、奖励 0 的空壳，正是 `tmp_1` 事故的形态。
 */
export const createPuzzleApi = (v: number, expectedRevision: number, puzzle: import("./puzzle").PuzzleBody) =>
  send<{ revision: number; puzzleId: string; seq: number }>("POST", `/config/versions/${v}/puzzles`,
    { expected_revision: expectedRevision, puzzle });
/** 更新已有行。`puzzleId` 必须是服务端分配过的；新建走 createPuzzleApi */
export const putPuzzleApi = (
  v: number, expectedRevision: number, puzzleId: string, puzzle: import("./puzzle").PuzzleBody,
) =>
  send<{ revision: number }>("PUT", `/config/versions/${v}/puzzles/${seg(puzzleId)}`,
    { expected_revision: expectedRevision, puzzle: { ...puzzle, puzzleId } });
export const deletePuzzleApi = (v: number, expectedRevision: number, puzzleId: string) =>
  send<{ revision: number }>("DELETE", `/config/versions/${v}/puzzles/${seg(puzzleId)}`,
    { expected_revision: expectedRevision });

/*
 * ── idol 关卡相册 / VIP 照片 / VIP 视频（版本内，逐行 CRUD，同拼图）──
 * ID 由服务端分配（ph_ / vp_ / vv_）：新建走 POST 带全部内容、回传 ID；PUT / DELETE 只认服务端分配过的 ID。
 * 快照接口（/snapshot）暂时不带这三块，打开版本时由 App 并行单独拉（见 loadSnapshot）
 */
type AlbumList<R> = { configVersion: number; revision: number; status: string; rows: R[] };
export const fetchIdolPhotosOf = (v: number) =>
  get<AlbumList<import("./idol-album").ServerIdolPhoto>>(`/config/versions/${v}/idol-photos`);
export const createIdolPhotoApi = (v: number, expectedRevision: number, photo: import("./idol-album").IdolPhotoBody) =>
  send<{ revision: number; photoId: string }>("POST", `/config/versions/${v}/idol-photos`, { expected_revision: expectedRevision, photo });
export const putIdolPhotoApi = (v: number, expectedRevision: number, photoId: string, photo: import("./idol-album").IdolPhotoBody) =>
  send<{ revision: number }>("PUT", `/config/versions/${v}/idol-photos/${seg(photoId)}`,
    { expected_revision: expectedRevision, photo: { ...photo, photoId } });
export const deleteIdolPhotoApi = (v: number, expectedRevision: number, photoId: string) =>
  send<{ revision: number }>("DELETE", `/config/versions/${v}/idol-photos/${seg(photoId)}`, { expected_revision: expectedRevision });

export const fetchIdolVipPhotosOf = (v: number) =>
  get<AlbumList<import("./idol-album").ServerIdolVipPhoto>>(`/config/versions/${v}/idol-vip-photos`);
export const createIdolVipPhotoApi = (v: number, expectedRevision: number, vipPhoto: import("./idol-album").IdolVipPhotoBody) =>
  send<{ revision: number; vipPhotoId: string }>("POST", `/config/versions/${v}/idol-vip-photos`, { expected_revision: expectedRevision, vipPhoto });
export const putIdolVipPhotoApi = (v: number, expectedRevision: number, vipPhotoId: string, vipPhoto: import("./idol-album").IdolVipPhotoBody) =>
  send<{ revision: number }>("PUT", `/config/versions/${v}/idol-vip-photos/${seg(vipPhotoId)}`,
    { expected_revision: expectedRevision, vipPhoto: { ...vipPhoto, vipPhotoId } });
export const deleteIdolVipPhotoApi = (v: number, expectedRevision: number, vipPhotoId: string) =>
  send<{ revision: number }>("DELETE", `/config/versions/${v}/idol-vip-photos/${seg(vipPhotoId)}`, { expected_revision: expectedRevision });

export const fetchIdolVipVideosOf = (v: number) =>
  get<AlbumList<import("./idol-album").ServerIdolVipVideo>>(`/config/versions/${v}/idol-vip-videos`);
export const createIdolVipVideoApi = (v: number, expectedRevision: number, vipVideo: import("./idol-album").IdolVipVideoBody) =>
  send<{ revision: number; vipVideoId: string }>("POST", `/config/versions/${v}/idol-vip-videos`, { expected_revision: expectedRevision, vipVideo });
export const putIdolVipVideoApi = (v: number, expectedRevision: number, vipVideoId: string, vipVideo: import("./idol-album").IdolVipVideoBody) =>
  send<{ revision: number }>("PUT", `/config/versions/${v}/idol-vip-videos/${seg(vipVideoId)}`,
    { expected_revision: expectedRevision, vipVideo: { ...vipVideo, vipVideoId } });
export const deleteIdolVipVideoApi = (v: number, expectedRevision: number, vipVideoId: string) =>
  send<{ revision: number }>("DELETE", `/config/versions/${v}/idol-vip-videos/${seg(vipVideoId)}`, { expected_revision: expectedRevision });

/*
 * ── 关卡多图（改版）：关卡 / 关卡图片（版本内，逐行 CRUD，ID 服务端分配 st_ / si_）──
 * 删一关时服务端同事务连带删掉它的图，回包带 deletedImageIds，保存流程据此把这些图从基线里摘掉、不再逐张 DELETE
 */
export const createStageApi = (v: number, expectedRevision: number, stage: import("./stage-level").StageBody) =>
  send<{ revision: number; stageId: string }>("POST", `/config/versions/${v}/stages`, { expected_revision: expectedRevision, stage });
export const putStageApi = (v: number, expectedRevision: number, stageId: string, stage: import("./stage-level").StageBody) =>
  send<{ revision: number }>("PUT", `/config/versions/${v}/stages/${seg(stageId)}`,
    { expected_revision: expectedRevision, stage: { ...stage, stageId } });
export const deleteStageApi = (v: number, expectedRevision: number, stageId: string) =>
  send<{ revision: number; deletedImageIds?: string[] }>("DELETE", `/config/versions/${v}/stages/${seg(stageId)}`, { expected_revision: expectedRevision });

export const createStageImageApi = (v: number, expectedRevision: number, image: import("./stage-level").StageImageBody) =>
  send<{ revision: number; imageId: string }>("POST", `/config/versions/${v}/stage-images`, { expected_revision: expectedRevision, image });
export const putStageImageApi = (v: number, expectedRevision: number, imageId: string, image: import("./stage-level").StageImageBody) =>
  send<{ revision: number }>("PUT", `/config/versions/${v}/stage-images/${seg(imageId)}`,
    { expected_revision: expectedRevision, image: { ...image, imageId } });
export const deleteStageImageApi = (v: number, expectedRevision: number, imageId: string) =>
  send<{ revision: number }>("DELETE", `/config/versions/${v}/stage-images/${seg(imageId)}`, { expected_revision: expectedRevision });

// ── 领养池（版本内）。整表替换：V1 只有一段，逐行接口在一张只有一行的表上只是多维护三个端点 ──
export const fetchAdoptPoolsOf = (v: number) =>
  get<{
    configVersion: number; revision: number; status: string;
    pools: import("./adoptPool").AdoptPool[];
    /** 本版本的角色名册，给成员表当行源——运营是按名字配权重的，不是按 pet_id */
    pets: import("./adoptPool").AdoptRosterPet[];
  }>(`/config/versions/${v}/adopt-pools`);
export const putAdoptPoolsApi = (v: number, expectedRevision: number, pools: import("./adoptPool").AdoptPool[]) =>
  send<{ revision: number }>("PUT", `/config/versions/${v}/adopt-pools`,
    { expected_revision: expectedRevision, pools });

// ── 升星档（版本内，整表替换）。上限与满级发放行为是 cfg_main_common 的列，走 common 那条 PATCH ──
export const fetchPetStarsOf = (v: number) =>
  get<{ configVersion: number; revision: number; status: string;
    stars: import("./petStar").PetStarStep[];
    /** 只读回显：全局星级上限，改它在同一页上但走 common 的 PATCH */
    petStarMax: number;
  }>(`/config/versions/${v}/pet-stars`);
export const putPetStarsApi = (v: number, expectedRevision: number, stars: import("./petStar").PetStarStep[]) =>
  send<{ revision: number }>("PUT", `/config/versions/${v}/pet-stars`,
    { expected_revision: expectedRevision, stars });

// ── idol 亲密度（版本内）：等级曲线 + 来源一起整表替换；单个 idol 每日上限是 common 的列，走 common 那条 PATCH ──
export const fetchIntimacyOf = (v: number) =>
  get<{ configVersion: number; revision: number; status: string;
    levels: import("./intimacy").IntimacyLevel[];
    sources: import("./intimacy").IntimacySource[];
    /** 来源注册表（代码定义）：下拉只能从这里选，available=false 的标灰 */
    sourceDefs: import("./intimacy").IntimacySourceDef[];
  }>(`/config/versions/${v}/intimacy`);
export const putIntimacyApi = (v: number, expectedRevision: number,
  levels: import("./intimacy").IntimacyLevel[], sources: import("./intimacy").IntimacySource[]) =>
  send<{ revision: number }>("PUT", `/config/versions/${v}/intimacy`,
    { expected_revision: expectedRevision, levels, sources });

// ── 喵汪角色（版本内）。编号由运营手填，所以新建也带 petId，撞号服务端报错 ──
export const fetchPetsOf = (v: number) =>
  get<{ configVersion: number; revision: number; status: string;
    // PetRead 而不是 PetBody：读回来的每一行都**必须**带 published（服务端算的首发身份）。
    // 声明成可缺省的话，漏传时页面会静默把所有角色当没发布过——锁全开、删除按钮全亮，
    // 而类型检查一声不响。安全相关的标记不做兼容回落
    pets: import("./pet").PetRead[] }>(
    `/config/versions/${v}/pets`);
export const createPetApi = (v: number, expectedRevision: number, pet: import("./pet").PetBody) =>
  send<{ revision: number; petId: string }>("POST", `/config/versions/${v}/pets`,
    { expected_revision: expectedRevision, pet });
/** 更新已有行。编号是主键的一部分、不可改；改编号 = 删一条 + 建一条 */
export const putPetApi = (v: number, expectedRevision: number, petId: string, pet: import("./pet").PetBody) =>
  send<{ revision: number }>("PUT", `/config/versions/${v}/pets/${seg(petId)}`,
    { expected_revision: expectedRevision, pet: { ...pet, petId } });
export const deletePetApi = (v: number, expectedRevision: number, petId: string) =>
  send<{ revision: number }>("DELETE", `/config/versions/${v}/pets/${seg(petId)}`,
    { expected_revision: expectedRevision });

/** 全局价目表。整表替换——十几行，逐行 CAS 不值当 */
export const fetchPuzzlePricesOf = (v: number) =>
  get<{ configVersion: number; revision: number; status: string; prices: import("./puzzle").PuzzlePrice[] }>(
    `/config/versions/${v}/puzzle-prices`);
export const putPuzzlePricesApi = (v: number, expectedRevision: number, rows: import("./puzzle").PuzzlePrice[]) =>
  send<{ revision: number }>("PUT", `/config/versions/${v}/puzzle-prices`,
    { expected_revision: expectedRevision, replace_all: true, rows });

// ── 每日奖励：周期档 + 累计档整表读写（表很小，逐行 CRUD 反而让「删第 7 天」拆成两步） ──
export const fetchDailyRewardsOf = (v: number) =>
  get<{ configVersion: number; revision: number; status: string } & import("./dailyReward").DailyRewards>(
    `/config/versions/${v}/daily-rewards`);
export const putDailyRewardsApi = (v: number, expectedRevision: number, body: import("./dailyReward").DailyRewards) =>
  send<{ revision: number }>("PUT", `/config/versions/${v}/daily-rewards`,
    { expected_revision: expectedRevision, days: body.days, milestones: body.milestones });

// ── 社交 / 个人中心：加入我们渠道 + 时间线事件类型（同上，两张小表整表替换） ──
export const fetchSocialChannelsOf = (v: number) =>
  get<{ configVersion: number; revision: number; status: string; channels: import("./social").SocialChannel[] }>(
    `/config/versions/${v}/social-channels`);
export const putSocialChannelsApi = (v: number, expectedRevision: number, channels: import("./social").SocialChannel[]) =>
  send<{ revision: number }>("PUT", `/config/versions/${v}/social-channels`,
    { expected_revision: expectedRevision, channels });
export const fetchSocialEventTypesOf = (v: number) =>
  get<{ configVersion: number; revision: number; status: string; types: import("./social").SocialEventType[] }>(
    `/config/versions/${v}/social-event-types`);
export const putSocialEventTypesApi = (v: number, expectedRevision: number, types: import("./social").SocialEventType[]) =>
  send<{ revision: number }>("PUT", `/config/versions/${v}/social-event-types`,
    { expected_revision: expectedRevision, types });

// ── 头像框名册（版本内，整表替换；发放走奖励项 kind=frame）──
export const fetchFramesOf = (v: number) =>
  get<{ configVersion: number; revision: number; status: string; frames: import("./frame").AvatarFrame[] }>(
    `/config/versions/${v}/frames`);
export const putFramesApi = (v: number, expectedRevision: number, frames: import("./frame").AvatarFrame[]) =>
  send<{ revision: number }>("PUT", `/config/versions/${v}/frames`,
    { expected_revision: expectedRevision, frames });

// ── 多人匹配棋盘格位表（版本内，整表替换；下发走资源块 board:cells）──
export const fetchBoardCellsOf = (v: number) =>
  get<{ configVersion: number; revision: number; status: string; cells: import("./board-cells").BoardCell[] }>(
    `/config/versions/${v}/board-cells`);
export const putBoardCellsApi = (v: number, expectedRevision: number, cells: import("./board-cells").BoardCell[]) =>
  send<{ revision: number }>("PUT", `/config/versions/${v}/board-cells`,
    { expected_revision: expectedRevision, cells });
/** 神灯掉落表（cfg_lamp_drop）：整表替换，判法以服务端为准 */
export const fetchLampDropsOf = (v: number) =>
  get<{ configVersion: number; revision: number; status: string; drops: import("./lamp-drops").LampDrop[] }>(`/config/versions/${v}/lamp-drops`);
export const putLampDropsApi = (v: number, expectedRevision: number, drops: import("./lamp-drops").LampDrop[]) =>
  send<{ revision: number }>("PUT", `/config/versions/${v}/lamp-drops`, { expected_revision: expectedRevision, drops });

/* ── idol 名册（**不随 config_version**，走行级 row_version CAS） ── */

export interface IdolListPage {
  total: number; page: number; page_size: number;
  idols: import("./idol").Idol[];
}
/** 每页条数。服务端上限也是 200（admin/idol.ts 的 listIdols） */
export const IDOL_PAGE_SIZE = 200;

export interface IdolQuery { id?: string; stageName?: string; agency?: string; status?: string; page?: number }

export const fetchIdols = (q: IdolQuery = {}) =>
  get<IdolListPage>("/idols", {
    ...(q.id ? { id: q.id } : {}),
    ...(q.stageName ? { stage_name: q.stageName } : {}),
    ...(q.agency ? { agency: q.agency } : {}),
    ...(q.status ? { status: q.status } : {}),
    page: String(q.page ?? 1),
    page_size: String(IDOL_PAGE_SIZE),
  });

/**
 * 路径段一律 `encodeURIComponent`。ID 在服务端已限制成 `[A-Za-z0-9_-]`，
 * 但**存量数据不受新规则约束** —— 编码一次是零成本的，靠「入口应该拦住了」
 * 来省它，出问题时是一条拆坏的路由，不是一条报错。
 */
const seg = (v: string) => encodeURIComponent(v);

/**
 * 新建 idol。**ID 由服务端分配**，前端不传也不猜；内容一次带齐 ——
 * 不做「先落一条空行再补」，那样点一下又取消就在名册里留一条空记录。
 */
export const createIdolApi = (idol: Record<string, unknown>) =>
  send<{ idolId: string; rowVersion: number }>("POST", "/idols", { idol });

export const fetchIdol = (idolId: string) => get<import("./idol").IdolDetail>(`/idols/${seg(idolId)}`);

/**
 * 整行保存。**body 里绝不能带 status** —— 服务端会直接拒。
 * 上下架走下面的 patchIdolStatusApi，理由见 src/admin/idol.ts 的 IdolIn 注释。
 */
/** 整行编辑。新增走 createIdolApi —— 这里 expected_row_version 必须 ≥ 1 */
export const putIdolApi = (expectedRowVersion: number, idol: Record<string, unknown>) =>
  send<{ rowVersion: number }>("PUT", `/idols/${seg(String(idol.idolId))}`,
    { expected_row_version: expectedRowVersion, idol });

/**
 * 后端生成 idol 二维码（src/admin/idol-qr.ts）：画好、传好 CDN，回图片地址与码里的接口地址。
 * 不写库——抽屉把 qrImg 换上，随整行保存
 */
export const generateIdolQrApi = (idolId: string) =>
  send<{ qrImg: string; qrUrl: string }>("POST", `/idols/${seg(idolId)}/qr`, {});

/** 本代理全部 idol 重新生成二维码并直接写库；中途被别人改过的行跳过，列在 failed 里 */
export const regenerateAllIdolQrApi = () =>
  send<{ total: number; updated: number; failed: Array<{ idolId: string; reason: string }> }>("POST", "/idols-qr/regenerate", {});

export const patchIdolStatusApi = (idolId: string, expectedRowVersion: number, status: string) =>
  send<{ rowVersion: number }>("PATCH", `/idols/${seg(idolId)}/status`,
    { expected_row_version: expectedRowVersion, status });

export const deleteIdolApi = (idolId: string, expectedRowVersion: number) =>
  send<{ rowVersion: number }>("DELETE", `/idols/${seg(idolId)}`, { expected_row_version: expectedRowVersion });

export const putIdolLicenseApi = (idolId: string, expectedRowVersion: number, license: Record<string, unknown>) =>
  send<{ rowVersion: number; created: boolean }>("PUT", `/idols/${seg(idolId)}/licenses/${seg(String(license.batchId))}`,
    { expected_row_version: expectedRowVersion, license });

/** 撤销（软删除）。被生效版本的拼图引用时服务端会拒并列出引用方（不筛启用、不看 idol 状态） */
export const revokeIdolLicenseApi = (idolId: string, batchId: string, expectedRowVersion: number) =>
  send<{ ok: boolean }>("DELETE", `/idols/${seg(idolId)}/licenses/${seg(batchId)}`,
    { expected_row_version: expectedRowVersion });

/* ── 数据集与竞品网格（只读） ── */

export interface DatasetItem {
  kind: "version" | "ref" | "formula";
  id: string;
  name: string;
  note?: string;
}
export const fetchDatasets = () => get<{ items: DatasetItem[] }>("/datasets");

export interface RivalCostsPayload {
  datasetId: number;
  name: string;
  source: string;
  checksum: string;
  /** 稀疏：竞品未覆盖的关不出现 */
  rows: Array<{ level: number; buildingIdx: number; star: number; cost: Money }>;
}
export const fetchRivalCosts = (datasetId: number) => get<RivalCostsPayload>(`/rivals/${datasetId}/costs`);

/** 关卡配置（按当前 A0 从对应库读；缺省读生效版本，带 version 读指定版本） */
export interface ServerBuilding {
  idx: number; name: string; nameI18n: Record<string, string> | null; main: boolean;
  /** 相对关卡中心点的摆位偏移（建造底图坐标系像素，x 右为正 / y 下为正）。0 = 未摆位 */
  offsetX: number; offsetY: number;
}
export interface ServerLevel {
  level: number;
  /** 稳定身份（UUID）；老服务端没有 */
  uid?: string;
  name: string;
  nameI18n: Record<string, string> | null;
  stage: string;
  stageI18n: Record<string, string> | null;
  rollsRangePct: number;
  actualC: Money | null;
  coverImg: string;
  bgImg: string;
  bldInit: Money[] | null;
  bValue: Money | null;
  targetRolls: Money | null;
  targetRollsOverride?: Money | null;
  /** 单掷金币 g（千分之一金币的十进制串）；null = 走曲线。老服务端没有 */
  gMilli?: string | null;
  costMatrixSeed?: number | null;
  nBuildings: number;
  nLevels: number;
  /** 万分数：10000 = 1.0 */
  starShapeBps: number[];
  buildings: ServerBuilding[];
  /** cells[b-1][l-1]；老版本无逐格价时为 null，残缺格为 0 */
  cells: Money[][] | null;
  /** 逐格素材，键 `"<建筑>:<星级>"`，稀疏。老版本没有这张表时缺席 */
  assets?: Record<string, { img: string; anim: string; damaged: string }>;
  /** 关卡资源三槽位（bg/blank/mid）原始配置，不含继承解析 */
  levelAssets?: Record<string, { img?: string; from?: number }>;
}
export interface LevelsPayload {
  configVersion: number;
  revision: number;
  status: string;
  levels: ServerLevel[];
}

/**
 * 单格素材写入。省略 imgUrl / animUrl 表示该类不动，传空串才是清除。
 * upsert 语义——不是集合替换，两个人同时传不同格不会互相抹掉。
 */
export const putCellAssetApi = (
  v: number, level: number, b: number, star: number, expectedRevision: number,
  patch: { imgUrl?: string; animUrl?: string; damagedImgUrl?: string },
) =>
  send<{ revision: number }>("PUT", `/config/versions/${v}/levels/${level}/cells/${b}/${star}/asset`, {
    expected_revision: expectedRevision,
    ...(patch.imgUrl === undefined ? {} : { img_url: patch.imgUrl }),
    ...(patch.animUrl === undefined ? {} : { anim_url: patch.animUrl }),
    ...(patch.damagedImgUrl === undefined ? {} : { damaged_img_url: patch.damagedImgUrl }),
  });

/** 删草稿 = 移进回收站（7 天内可恢复）。只删 draft、不删生效中、带 revision CAS、有别人的改动不许删——都在服务端 deleteVersion 里 */
export const deleteVersionApi = (v: number, expectedRevision: number) =>
  send<{ trashed: true; purgeAtMs: number }>("DELETE", `/config/versions/${v}`, { expected_revision: expectedRevision });

export interface TrashRow {
  configVersion: number;
  note: string | null;
  author: string;
  createdAtMs: number;
  trashedAtMs: number;
  trashedBy: string;
  /** 过了这个时刻内容就被清掉、不能再恢复 */
  purgeAtMs: number;
  /** 这份草稿上有过实际改动的人与次数，多的在前 */
  editors: Array<{ actor: string; n: number }>;
}
/** 回收站：7 天内删掉的草稿 */
export const fetchTrash = () => get<TrashRow[]>("/config/trash");
/** 从回收站恢复成草稿，内容原样 */
export const restoreVersionApi = (v: number) => send<{ configVersion: number; revision: number }>("POST", `/config/versions/${v}/restore`, {});

/* ── 联调追踪（超管专用）────────────────────────────────────────────── */

export interface TraceRule { path: string; enabled: boolean; updatedBy: string; updatedAtMs: number }
export interface TracePayload {
  /** 服务端判定的身份。前端据它决定菜单显不显示——但拦截以服务端为准 */
  is_super_admin: boolean;
  rules: TraceRule[];
  /** 可勾选的接口清单，取自游戏端运行时 spec；拉不到时为空数组，不是错误 */
  paths: string[];
  /**
   * CAS 基准：服务端算的**全表**最新改动时刻。
   * **不要从 rules 里自己算**——rules 是筛过的展示数据，少一行基准就偏了，
   * 表现是保存永远被拒且刷新无效。
   */
  since: number;
  /** 玩家过滤名单；非空时只推名单内玩家的请求。空 = 不过滤 */
  player_filter: string[];
  /** 是否根超管（配在环境变量）。只有根超管能增删超管名单 */
  is_root?: boolean;
}
export const fetchTrace = () => get<TracePayload>("/debug-trace");

/** 超管名单（ops_super_admin）增删，只有根超管能调。identifier 是用户名或中台 cid */
export const addSuperAdmin = (identifier: string) =>
  send<{ added: string }>("POST", "/super-admins", { identifier });
export const removeSuperAdmin = (identifier: string) =>
  send<{ removed: string }>("DELETE", `/super-admins/${encodeURIComponent(identifier)}`);

/** 管端自有账号（独立登录，不依赖中台）。登录不带任何票，直接打 /login */
export interface AccountLoginResult { token: string; expires_in: number; username: string; is_super: boolean }
export async function loginWithPassword(username: string, password: string): Promise<AccountLoginResult> {
  const res = await fetch("/admin/api/login", {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ username, password }),
  });
  const body = (await res.json().catch(() => null)) as Envelope<AccountLoginResult> | null;
  if (!body) throw new ApiError(`HTTP ${res.status}`, String(res.status));
  if (body.code !== "0") throw new ApiError(body.message || `HTTP ${res.status}`, body.code, body.data);
  return body.data;
}
/**
 * Discord 登录的最后一步：notify-hub 重定向回来时带的一次性 token 换管端票。
 * 与 loginWithPassword 一样是裸 fetch —— 这时候手上还没有票可带。
 */
export async function loginWithOneTimeToken(token: string): Promise<AccountLoginResult> {
  const res = await fetch("/admin/api/login/token", {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token }),
  });
  const body = (await res.json().catch(() => null)) as Envelope<AccountLoginResult> | null;
  if (!body) throw new ApiError(`HTTP ${res.status}`, String(res.status));
  if (body.code !== "0") throw new ApiError(body.message || `HTTP ${res.status}`, body.code, body.data);
  return body.data;
}

/** Discord 绑定会话（服务端代调 notify-hub，前端自己画界面；见 components/DiscordBindFlow） */
export interface DiscordBindSession {
  session: string;
  status: "pending" | "bound" | "expired";
  code: string;
  command: string;
  invite_url: string;
  /** 秒级时间戳 */
  expires_at: number;
  discord_username: string;
}
export const discordBindStart = () => send<DiscordBindSession>("POST", "/discord/bind/session", {});
export const discordBindPoll = (session: string) => get<DiscordBindSession>("/discord/bind/session", { session });
export const discordBindRefresh = (session: string) => send<DiscordBindSession>("POST", "/discord/bind/session/refresh", { session });
export const discordUnbind = () => send<Record<string, never>>("POST", "/discord/unbind", {});
/** 当前登录的人绑没绑 Discord。是服务端推断的（用 Discord 登录成功过就算绑过），configured=false 表示这个环境没接 notify-hub */
export const discordBindStatus = () => get<{ configured: boolean; bound: boolean }>("/discord/bind/status");

/** 教程学习进度：上报自己的（按票里的人记），取所有人的（进度排名在前端按当前课程清单算） */
export interface LearningProgressRow { username: string; lesson_id: string; position: number; duration: number; completed: boolean; updated_at: number }
export const pushLearningProgress = (lessons: Omit<LearningProgressRow, "username">[]) =>
  send<{ saved: number }>("PUT", "/learning/progress", { lessons });
export const fetchLearningProgress = () => get<{ rows: LearningProgressRow[] }>("/learning/progress");

export interface AdminAccount {
  id: number; username: string; status: number; is_super: boolean; created_by: string;
  create_time: number; update_time: number; last_login_time: number | null; last_login_ip: string | null;
}
export const fetchAccounts = () => get<{ accounts: AdminAccount[] }>("/accounts");
export const createAccount = (body: { username: string; password: string; is_super: boolean }) =>
  send<AdminAccount>("POST", "/accounts", body);
export const updateAccount = (id: number, body: { status?: 0 | 1; is_super?: boolean; password?: string }) =>
  send<AdminAccount>("PUT", `/accounts/${id}`, body);
export const deleteAccount = (id: number) => send<{ deleted: number }>("DELETE", `/accounts/${id}`);
/** 管端用户：自建账号、Discord、中台换票登录过的人按用户名合成一人一行（服务端 admin/admin-users.ts） */
export type AdminUserSource = "account" | "discord" | "sso";
export interface AdminUser {
  username: string; display_name: string; note: string; disabled: boolean;
  /** 只是超管名单 / 根超管环境变量里的标识（多半是中台 cid），对不上登录过的人：只能取消超管 */
  list_only: boolean;
  account_id: number | null; sources: AdminUserSource[]; is_root: boolean;
  super_via: Array<"root" | "list" | "account">;
  last_seen_ms: number | null; last_login_ip: string | null; created_by: string; create_time: number | null;
  profile_updated_by: string; profile_updated_ms: number | null;
}
export const fetchAdminUsers = () => get<{ users: AdminUser[]; viewer_is_root: boolean }>("/admin-users");
export const updateAdminUser = (username: string, body: { display_name?: string; note?: string; disabled?: boolean }) =>
  send<{ updated: string }>("PUT", `/admin-users/${encodeURIComponent(username)}`, body);
export const changeOwnPassword = (old_password: string, new_password: string) =>
  send<{ changed: true }>("POST", "/password", { old_password, new_password });
/** 整表替换：传当前要开启的全部路径。空数组 = 全关 */
/**
 * 整表替换：传当前要开启的全部路径。空数组 = 全关。
 *
 * `since` 是打开页面时读到的最新改动时刻，用来挡住「慢的开启请求覆盖掉刚做的
 * 紧急全关」。**全关不传它**——停推是纠错手段，必须永远能成功。
 */
export const putTrace = (paths: string[], since?: number, playerFilter?: string[]) =>
  send<{ enabled: number; known: number }>("PUT", "/debug-trace", {
    ...(paths.length ? { paths, since } : { paths }),
    // 玩家过滤：字段缺省 = 不动它；空数组 = 清除过滤
    ...(playerFilter ? { player_filter: playerFilter } : {}),
  });

/* ── 开发用玩家票（超管专用）─────────────────────────────────────── */

export interface DevUser {
  userId: string; nickname: string | null; avatar: string | null;
  lang: string | null; lastLoginAtMs: number | null;
}
export const fetchDevUsers = (a0: string, q: string) =>
  get<{ a0: string; users: DevUser[] }>("/dev-token/users", { a0, q, size: "100" });
export const signDevToken = (a0: string, userId: string, ttlSec: number) =>
  send<{ token: string; expiresAt: number; user: DevUser }>("POST", "/dev-token/sign", {
    a0, user_id: userId, ttl_sec: ttlSec,
  });

export interface DevPanelState {
  available: boolean; environment: string; enabled: boolean; grantable: string[];
  /** 新手宠物自动发放：只影响**新建档**的玩家，与这一页那个按钮开关互不相干 */
  boot_pets: { enabled: boolean; count: number };
}
export const fetchDevPanel = () => get<DevPanelState>("/dev-panel");
export const setDevPanel = (enabled: boolean) => send<{ enabled: boolean }>("PUT", "/dev-panel", { enabled });
/** 回的是**存进去的值**（只数会被截到合法区间），照它刷新界面，别回显填进去的 */
export const setBootPets = (enabled: boolean, count: number) =>
  send<{ enabled: boolean; count: number }>("PUT", "/dev-panel/boot-pets", { enabled, count });
export const devSetBalance = (userId: string, assetId: string, target: string) =>
  send<{ before: string; after: string; delta: string }>("POST", "/dev-panel/balance", {
    user_id: userId, asset_id: assetId, target,
  });
/**
 * 掷一次。`code = null` 是**不强制、真随机**（连掷造进度用它——强制掉落造出来的号，
 * 每一格都是人挑的，拿它看掉落分布只会自证其说）。
 * `victimUserId` 只在这一掷结算到对抗玩法格（雷云 / 抢金库）时生效。
 */
export const devNpcThunder = (userId: string) =>
  send<{ outcome: string; position: number | null; shield_used: number; coins_lost: string; attacker_id: string; victim_id: string }>("POST", "/dev-panel/npc-thunder", { user_id: userId });

export const devForceRoll = (
  userId: string, code: number | null, multiplier: number, victimUserId?: string | null,
) =>
  send<DevRollResult>("POST", "/dev-panel/roll", {
    user_id: userId, code, multiplier, victim_user_id: victimUserId || null,
  });

/** roll 响应里面板要读的那几个字段。其余原样进 JSON 框，不在这里逐个建模 */
export interface DevRollResult {
  seq?: number;
  drop?: {
    type?: string;
    code?: number;
    amount?: string;
    qty?: number;
    detail?: {
      versus?: {
        mode?: string; outcome?: string; victim?: string; mirror_of?: string;
        damaged_idx?: number; coins?: string; victim_loss?: string; subsidy?: string;
      };
      [k: string]: unknown;
    } | null;
  };
  balances?: Record<string, unknown>;
  /**
   * 雷云只发攻击机会（detail.attack），面板掷出来就顺手结算：这里是 POST /v1/game/attack 的回执，
   * versus 与抢金库的同形
   */
  attack?: {
    source?: string;
    versus?: {
      mode?: string; outcome?: string; victim?: string; mirror_of?: string;
      damaged_idx?: number; coins?: string; victim_loss?: string; subsidy?: string;
    };
    [k: string]: unknown;
  };
  [k: string]: unknown;
}

/**
 * 多人对局：某个玩家正在打的那一局，与他挂着的「下一次强制」（点数 / 落格 / 转盘扇区）。
 * 落格由服务端在他掷骰时按认定后的位置反推点数。见 src/admin/dev-match-rig.ts
 */
export interface DevMatchRig {
  room_id: string;
  phase: string;
  player_id: string;
  seat: number;
  dice_per_roll: number;
  my_turn: boolean;
  progress: number | null;
  /** 当前那一枚所在的绝对格号（0–55）；进了终点道是 null */
  cell: number | null;
  pieces_done: number;
  wheel_sectors: DevMatchRigRow[];
  /** 本局普通格引用的 spin 行：「按掉落行」落格的候选 */
  spin_targets: DevMatchRigRow[];
  /** 本局棋盘上可以「按格子类型」落格的类型与格号 */
  cell_types: Array<{ type: "MAGIC_LAMP" | "WHEEL"; cells: number[] }>;
  board_config_version: number | null;
  rig: { dice?: number[]; wheel_sector?: number; target_cell?: number; target_spin_code?: number; target_cell_type?: string } | null;
}
/** 一个 spin 行按钮：行号、奖励种类、用到它的格号；名字取自本局冻结版本的掉落表（读不到就没有） */
export interface DevMatchRigRow { code: number; kinds: string[]; cells: number[]; name?: string; name_i18n?: Record<string, string> }
export const fetchDevMatchRig = (userId: string) =>
  get<DevMatchRig>(`/dev-panel/match-rig?user_id=${encodeURIComponent(userId)}`);
/** 整条替换：两项都给 null = 清掉。回的是挂完之后房间里真实的状态 */
export const setDevMatchRig = (
  userId: string, dice: number[] | null, wheelSector: number | null, targetCell: number | null = null,
  targetSpinCode: number | null = null, targetCellType: "MAGIC_LAMP" | "WHEEL" | null = null,
) =>
  send<DevMatchRig>("POST", "/dev-panel/match-rig", {
    user_id: userId, dice, wheel_sector: wheelSector, target_cell: targetCell, target_spin_code: targetSpinCode, target_cell_type: targetCellType,
  });

/* 下面三条都是「把玩家拨回可测状态」：不发资产、不动余额，只清限流与节奏状态 */
/** 老拼图碎片按 idol 迁移（全租户、可重跑）：不分 idol 的碎片转到各玩家的当前 idol（2026-10-23） */
export const devMigrateIdolFrags = () =>
  send<{ players: number; moved: Array<{ user_id: string; idol_id: string; qty: string }>; failed: Array<{ user_id: string; error: string }> }>("POST", "/dev-panel/migrate-idol-frags", {});
export const devResetDaily = (userId: string) =>
  send<{ removed: number }>("POST", "/dev-panel/reset-daily", { user_id: userId });
export const devRepair = (userId: string) =>
  send<{ buildings: number; fires: number }>("POST", "/dev-panel/repair", { user_id: userId });
export const devResetRhythm = (userId: string) =>
  send<{ removed: number }>("POST", "/dev-panel/reset-rhythm", { user_id: userId });
/** 拨回 idol 拼图状态：全部 idol 的关卡进度、旧版写真拼图记录、亲密度清零（已迁入 DO 的玩家报错） */
export const devResetIdolPuzzle = (userId: string) =>
  send<{ stageImages: number; puzzles: number; intimacy: number }>("POST", "/dev-panel/reset-idol-puzzle", { user_id: userId });
export const devResetBuildings = (userId: string) =>
  send<{ buildings: number; cityLevel: number; logs: number }>(
    "POST", "/dev-panel/reset-buildings", { user_id: userId });
/** 清除数据：清成刚注册的新号，账号留着（已迁入 DO 的玩家报错）。tables = 每张表删掉的行数 */
export const devWipePlayer = (userId: string) =>
  send<{ tables: Record<string, number> }>("POST", "/dev-panel/wipe", { user_id: userId });
/** 注销账号：与玩家自己注销同一条路径；already = 之前就注销过 */
export const devCancelAccount = (userId: string) =>
  send<{ cancelled: boolean; already: boolean }>("POST", "/dev-panel/cancel-account", { user_id: userId });

/** 测试面板 · 亲密度造数：dev_panel 直加点数；选真实来源则走完整上限链路 */
export const devIntimacy = (userId: string, idolId: string, source: string, units: string, points: string, multiplierBps: string) =>
  send<{
    idol_id: string; source: string; gained: number; capped_by: string | null;
    exp: number; level: number; level_before: number; level_exp: number; next_level_exp: number | null;
    day_key: number; day_total: number;
  }>("POST", "/dev-panel/intimacy", {
    user_id: userId, idol_id: idolId, source,
    units: units.trim() === "" ? undefined : Number(units),
    points: points.trim() === "" ? undefined : Number(points),
    multiplier_bps: multiplierBps.trim() === "" ? undefined : Number(multiplierBps),
  });

/** 测试面板 · 天赋造数：设成领到第 claimed 个节点（1 起，只能往上加）。返回设之前 / 之后已领几个、本版本共几个节点 */
export const devSetTalent = (userId: string, claimed: number) =>
  send<{ user_id: string; before: number; claimed: number; total: number; granted_nodes: string[] }>("POST", "/dev-panel/talent", {
    user_id: userId, claimed,
  });
/** 测试面板 · 调战阶胜场：setWins / add 二选一，都空 = 只查。只影响战阶 / 升阶 / 道具卡，不动战绩 */
export const devBattleWins = (userId: string, setWins: string, add: string) =>
  send<{ user_id: string; real_wins: number; adjust: number; wins: number }>("POST", "/dev-panel/battle-wins", {
    user_id: userId,
    // 非数字原样传给服务端报参数错误，不转成 NaN（会被序列化成 null、读成「没填」）
    set_wins: setWins.trim() === "" ? undefined : /^\d+$/.test(setWins.trim()) ? Number(setWins.trim()) : setWins,
    add: add.trim() === "" ? undefined : /^-?\d+$/.test(add.trim()) ? Number(add.trim()) : add,
  });

/** 测试面板 · 给玩家发宠物：qty 加到这一种上；star 给了就直接设成这个星级 */
export const devGrantPet = (userId: string, petId: string, qty: string, star: string) =>
  send<{ pet_id: string; qty: number; star: number; dup_count: number; is_new: boolean }>("POST", "/dev-panel/pets", {
    user_id: userId, pet_id: petId,
    // 填了却不是数（「5★」、全角）原样传给服务端让它报参数错误——转成 Number 会变 NaN、序列化成 null，
    // 被服务端读成「没填」，于是坏输入静默变成另一次造数
    qty: qty.trim() === "" ? 0 : /^\d+$/.test(qty.trim()) ? Number(qty.trim()) : qty,
    star: star.trim() === "" ? null : /^\d+$/.test(star.trim()) ? Number(star.trim()) : star,
  });

export const fetchLevelsOf = (version?: number) =>
  get<LevelsPayload | null>("/levels", version != null ? { version: String(version) } : undefined);

// ── 多人对战：匹配与房间参数 / 战阶 / 道具卡（三张小表，整表替换）──
export const fetchMatchModesOf = (v: number) =>
  get<import("./match").MatchMode[]>(`/config/versions/${v}/match-modes`);
export const putMatchModesApi = (v: number, expectedRevision: number, modes: import("./match").MatchMode[]) =>
  send<{ revision: number }>("PUT", `/config/versions/${v}/match-modes`, { expected_revision: expectedRevision, modes });
export const fetchBattleStagesOf = (v: number) =>
  get<import("./match").BattleStage[]>(`/config/versions/${v}/battle-stages`);
export const putBattleStagesApi = (v: number, expectedRevision: number, stages: import("./match").BattleStage[]) =>
  send<{ revision: number }>("PUT", `/config/versions/${v}/battle-stages`, { expected_revision: expectedRevision, stages });
export const fetchAbilityCardsOf = (v: number) =>
  get<import("./match").AbilityCard[]>(`/config/versions/${v}/ability-cards`);
export const putAbilityCardsApi = (v: number, expectedRevision: number, cards: import("./match").AbilityCard[]) =>
  send<{ revision: number }>("PUT", `/config/versions/${v}/ability-cards`, { expected_revision: expectedRevision, cards });

// ── 营地天赋树节点（萌宠小镇，整表替换）──
export const fetchTalentNodesOf = (v: number) =>
  get<{ configVersion: number; revision: number; status: string; nodes: import("./talent").TalentNode[] }>(`/config/versions/${v}/talent-nodes`);
export const putTalentNodesApi = (v: number, expectedRevision: number, nodes: import("./talent").TalentNode[]) =>
  send<{ revision: number }>("PUT", `/config/versions/${v}/talent-nodes`, { expected_revision: expectedRevision, nodes });

/** 一个块的结果：ok=false 时该块不可用（只读），其余块照常——与 allSettled 同语义 */
export type SnapshotBlock<T> = { ok: true; value: T } | { ok: false; error: string };
export interface SnapshotPayload {
  levels: LevelsPayload;
  blocks: {
    common: SnapshotBlock<Awaited<ReturnType<typeof fetchCommonOf>>>;
    rewards: SnapshotBlock<Awaited<ReturnType<typeof fetchRewardsOf>>>;
    packs: SnapshotBlock<Awaited<ReturnType<typeof fetchPacksOf>>>;
    auctions: SnapshotBlock<Awaited<ReturnType<typeof fetchAuctionsOf>>>;
    tiles: SnapshotBlock<Awaited<ReturnType<typeof fetchTilesOf>>>;
    spin: SnapshotBlock<Awaited<ReturnType<typeof fetchSpinOf>>>;
    puzzles: SnapshotBlock<Awaited<ReturnType<typeof fetchPuzzlesOf>>>;
    puzzlePrices: SnapshotBlock<Awaited<ReturnType<typeof fetchPuzzlePricesOf>>>;
    dailyRewards: SnapshotBlock<Awaited<ReturnType<typeof fetchDailyRewardsOf>>>;
    socialChannels: SnapshotBlock<Awaited<ReturnType<typeof fetchSocialChannelsOf>>>;
    socialEventTypes: SnapshotBlock<Awaited<ReturnType<typeof fetchSocialEventTypesOf>>>;
    pets: SnapshotBlock<Awaited<ReturnType<typeof fetchPetsOf>>>;
    adoptPools: SnapshotBlock<Awaited<ReturnType<typeof fetchAdoptPoolsOf>>>;
    /** 升星档块。老服务端没带它时整块缺失，前端按「不可用」处理 */
    petStars?: SnapshotBlock<Awaited<ReturnType<typeof fetchPetStarsOf>>>;
    pettownStars: SnapshotBlock<Awaited<ReturnType<typeof fetchPettownStarsOf>>>;
    pettownSlots: SnapshotBlock<Awaited<ReturnType<typeof fetchPettownSlotsOf>>>;
    matchModes: SnapshotBlock<Awaited<ReturnType<typeof fetchMatchModesOf>>>;
    abilityCards: SnapshotBlock<Awaited<ReturnType<typeof fetchAbilityCardsOf>>>;
    battleStages: SnapshotBlock<Awaited<ReturnType<typeof fetchBattleStagesOf>>>;
    /** 新加的块：服务端还没带上它时当「块不可用」 */
    talentNodes?: SnapshotBlock<Awaited<ReturnType<typeof fetchTalentNodesOf>>>;
    /** 头像框块是后加的：老服务端没带它时按「块不可用」处理（App 里 settle 时兜底） */
    frames?: SnapshotBlock<Awaited<ReturnType<typeof fetchFramesOf>>>;
    /** 多人匹配棋盘格位表；老服务端没带它时当块不可用 */
    boardCells?: SnapshotBlock<Awaited<ReturnType<typeof fetchBoardCellsOf>>>;
    lampDrops?: SnapshotBlock<Awaited<ReturnType<typeof fetchLampDropsOf>>>;
    /** idol 亲密度：等级曲线 + 来源 + 来源注册表。老服务端没带它时当块不可用 */
    intimacy?: SnapshotBlock<Awaited<ReturnType<typeof fetchIntimacyOf>>>;
    /** idol 关卡相册 / VIP 照片 / VIP 视频。快照接口还没带它们时，App 并行单独拉这三块 */
    idolPhotos?: SnapshotBlock<Awaited<ReturnType<typeof fetchIdolPhotosOf>>>;
    idolVipPhotos?: SnapshotBlock<Awaited<ReturnType<typeof fetchIdolVipPhotosOf>>>;
    idolVipVideos?: SnapshotBlock<Awaited<ReturnType<typeof fetchIdolVipVideosOf>>>;
    /** 关卡多图（改版）：关卡与关卡图片两块；老服务端没带时当「块不可用」 */
    stages?: SnapshotBlock<{ configVersion: number; revision: number; status: string; rows: import("./stage-level").ServerStage[] }>;
    stageImages?: SnapshotBlock<{ configVersion: number; revision: number; status: string; rows: import("./stage-level").ServerStageImage[] }>;
    /** 竞品列（随版本存）：列定义 + 关卡制逐格价 + 萌宠逐星价。老服务端没有这一块 */
    rivals?: SnapshotBlock<RivalsPayload>;
  };
}
/** 关卡 + 14 个块一次拿齐（服务端并发拉、打包回来），替代打开版本时的 1 + 14 个请求 */
export const fetchSnapshotOf = (version: number) =>
  get<SnapshotPayload | null>(`/config/versions/${version}/snapshot`);

// ── 客户端自留数据（玩家管理 · 客户端数据页）──
export interface ClientStateRow { ns: string; k: string; v: string; ver: number; updated_at_ms: number }
export interface ClientStatePlayerRow { userIdHex: string; nickname: string | null; avatar: string | null; cityLevel: number; lastActiveMs: number; keyCount: number; lastWriteMs: number | null }
export const fetchClientStatePlayers = (q: { kw?: string; page?: number; size?: number }) =>
  get<{ rows: ClientStatePlayerRow[]; total: number; page: number; size: number }>("/client-state/players", {
    ...(q.kw ? { kw: q.kw } : {}), page: String(q.page ?? 1), size: String(q.size ?? 20),
  });
export const fetchClientState = (userIdHex: string) =>
  get<{ items: ClientStateRow[] }>(`/players/${encodeURIComponent(userIdHex)}/client-state`);
export const putClientStateValue = (userIdHex: string, ns: string, k: string, v: string) =>
  send<{ ver: number }>("PUT", `/players/${encodeURIComponent(userIdHex)}/client-state`, { ns, k, v });
export const deleteClientStateKeys = (userIdHex: string, ns: string, sel: { keys: string[] } | { all: true }) =>
  send<{ deleted: number }>("DELETE", `/players/${encodeURIComponent(userIdHex)}/client-state`, { ns, ...sel });

/** GM 白名单：玩家侧调试能力（roll 的 force_code）的第二道闸。超管专用，只在 test 可用 */
export type GmStatus = "pending" | "approved" | "rejected";
export interface GmAccount {
  user_id: string; status: GmStatus; developer: string; note: string; enabled: boolean;
  created_by: string; created_at_ms: number; updated_by: string; updated_at_ms: number;
}
export interface GmListState { items: GmAccount[]; available: boolean; environment: string }
export const fetchGmAccounts = () => get<GmListState>("/gm");
export const upsertGmAccount = (a: { user_id: string; developer: string; note: string; enabled?: boolean }) =>
  send<{ ok: boolean }>("PUT", "/gm", a);
export const setGmEnabled = (userId: string, enabled: boolean) =>
  send<{ ok: boolean }>("POST", "/gm/enabled", { user_id: userId, enabled });
export const deleteGmAccount = (userId: string) =>
  send<{ ok: boolean }>("DELETE", `/gm/${encodeURIComponent(userId)}`);
export const reviewGmAccount = (userId: string, approve: boolean) =>
  send<{ ok: boolean }>("POST", "/gm/review", { user_id: userId, approve });

/* ── 商城商品（管端上架、C 端钻石购买）────────────────────────────────── */

export type ShopContent = { asset_id: string; qty: number };
export interface ShopItemRow {
  itemId: string; name: string; nameI18n: Record<string, string>; coverImg: string; iconImg: string;
  sectionId: string; card: "banner" | "grid" | string;
  priceKind: "gem" | "coin" | "free" | "cash" | string; priceGem: number; priceCoin: Money; cashPrice: { currency: string; amount: number } | null;
  contents: ShopContent[]; firstBonus: ShopContent[] | null;
  badgeKind: "" | "value_pct" | "text" | string; badgeValue: number; badgeTextI18n: Record<string, string> | null;
  descI18n: Record<string, string> | null;
  limitPerUser: number; limitPeriod: "lifetime" | "daily" | "weekly" | "monthly" | string; unlockLevel: number;
  kind: "once" | "subscription" | string; durationDays: number; dailyContents: ShopContent[] | null;
  startAtMs: number | null; endAtMs: number | null;
  status: "on" | "off" | string;
  sortOrder: number; rowVersion: number; createdBy: string; updatedBy: string; createdAtMs: number; updatedAtMs: number;
}
export type ShopItemIn = Pick<ShopItemRow,
  "nameI18n" | "coverImg" | "iconImg" | "sectionId" | "card" | "priceKind" | "priceGem" | "priceCoin" | "cashPrice" | "contents" | "firstBonus" |
  "badgeKind" | "badgeValue" | "badgeTextI18n" | "descI18n" | "limitPerUser" | "limitPeriod" | "unlockLevel" | "kind" | "durationDays" |
  "dailyContents" | "startAtMs" | "endAtMs" | "sortOrder">;
export const fetchShopItems = (sectionId?: string) => get<{ items: ShopItemRow[] }>("/shop/items", sectionId != null ? { section_id: sectionId } : undefined);
export const createShopItemApi = (item: ShopItemIn) => send<{ itemId: string; rowVersion: number }>("POST", "/shop/items", { item });
export const putShopItemApi = (itemId: string, expectedRowVersion: number, item: ShopItemIn) =>
  send<{ rowVersion: number }>("PUT", `/shop/items/${encodeURIComponent(itemId)}`, { expected_row_version: expectedRowVersion, item });
export const patchShopItemStatusApi = (itemId: string, expectedRowVersion: number, status: "on" | "off") =>
  send<{ rowVersion: number }>("PATCH", `/shop/items/${encodeURIComponent(itemId)}/status`, { expected_row_version: expectedRowVersion, status });
export const deleteShopItemApi = (itemId: string, expectedRowVersion: number) =>
  send<{ ok: boolean }>("DELETE", `/shop/items/${encodeURIComponent(itemId)}`, { expected_row_version: expectedRowVersion });

/** 商城分区（商城 2.0）：左侧锚点 = 一个分区 */
export interface ShopSectionRow {
  sectionId: string; name: string; nameI18n: Record<string, string>; iconImg: string; gridCols: number;
  status: "on" | "off" | string; sortOrder: number; startAtMs: number | null; endAtMs: number | null;
  rowVersion: number; createdBy: string; updatedBy: string; createdAtMs: number; updatedAtMs: number;
}
export type ShopSectionIn = Pick<ShopSectionRow, "nameI18n" | "iconImg" | "gridCols" | "sortOrder" | "startAtMs" | "endAtMs">;
export const fetchShopSections = () => get<{ sections: ShopSectionRow[] }>("/shop/sections");
export const createShopSectionApi = (section: ShopSectionIn) => send<{ sectionId: string; rowVersion: number }>("POST", "/shop/sections", { section });
export const putShopSectionApi = (sectionId: string, expectedRowVersion: number, section: ShopSectionIn) =>
  send<{ rowVersion: number }>("PUT", `/shop/sections/${encodeURIComponent(sectionId)}`, { expected_row_version: expectedRowVersion, section });
export const patchShopSectionStatusApi = (sectionId: string, expectedRowVersion: number, status: "on" | "off") =>
  send<{ rowVersion: number }>("PATCH", `/shop/sections/${encodeURIComponent(sectionId)}/status`, { expected_row_version: expectedRowVersion, status });
export const deleteShopSectionApi = (sectionId: string, expectedRowVersion: number) =>
  send<{ ok: boolean }>("DELETE", `/shop/sections/${encodeURIComponent(sectionId)}`, { expected_row_version: expectedRowVersion });

/** 内购配置（src/admin/iap.ts）：各平台 App + 现金商品的商店商品 ID。商店密钥在配置里，不经管端 */
export type IapPlatform = "ios" | "android";
export interface IapAppIn { bundleId: string; appleAppId: number; allowSandbox: boolean; status: "on" | "off" }
export interface IapAppRow extends IapAppIn { platform: IapPlatform; updatedBy: string; updatedAtMs: number }
export interface IapProductIds { iapProductIos: string; iapProductAndroid: string }
export interface IapItemRow extends IapProductIds {
  itemId: string; name: string; nameI18n: Record<string, string>; iconImg: string; coverImg: string;
  status: string; kind: string; cashPrice: { currency: string; amount: number } | null; rowVersion: number;
  /** 空串 = 未归类；服务端已按分区归类、区内按价升序排好，页面直接用返回顺序 */
  sectionId: string; sectionName: string; sectionNameI18n: Record<string, string>; sectionStatus: string;
}
export interface IapConfig { apps: IapAppRow[]; items: IapItemRow[] }
export const fetchIapConfig = () => get<IapConfig>("/iap/config");
export const putIapAppApi = (platform: IapPlatform, app: IapAppIn) => send<{ ok: boolean }>("PUT", `/iap/apps/${platform}`, { app });
export const putIapItemApi = (itemId: string, expectedRowVersion: number, item: IapProductIds) =>
  send<{ rowVersion: number }>("PUT", `/iap/items/${encodeURIComponent(itemId)}`, { expected_row_version: expectedRowVersion, item });

// ── 物品资料（src/admin/asset-info.ts）：商城奖励物品详情弹窗的图标、名称、介绍、获取途径。活表，保存即生效 ──
/** 跳转白名单（与服务端 ASSET_JUMP_TYPES 同口径） */
export const ASSET_JUMP_TYPES = ["shop_section", "task", "puzzle", "idol"] as const;
export type AssetJumpType = (typeof ASSET_JUMP_TYPES)[number];
export const ASSET_TASK_TABS = ["daily", "weekly", "achievement", "newbie"] as const;
export interface AssetJumpTarget { type: AssetJumpType; params: Record<string, string> }
export interface AssetObtain { text_i18n: Record<string, string>; target: AssetJumpTarget | null }
export interface AssetInfoIn {
  /** iconImg = 单个图标；iconPluralImg = 复数图标（数量 > 1 时客户端用，空 = 没配，客户端回退单个图标） */
  iconImg: string; iconPluralImg: string; nameI18n: Record<string, string>; descI18n: Record<string, string>; obtain: AssetObtain[]; useTarget: AssetJumpTarget | null; showDetail: boolean;
}
export interface AssetInfoRow extends AssetInfoIn {
  assetType: string; assetId: string;
  /** 0 = 还没配过（列表补出来的空行），保存时带 0 即为新建 */
  rowVersion: number; updatedBy: string; updatedAtMs: number | null;
}
/** 跳转「商城分区」的候选：在架、且至少有一件在架商品 */
export interface AssetJumpSection { sectionId: string; name: string; nameI18n: Record<string, string> }
export interface AssetInfoConfig { rows: AssetInfoRow[]; sections: AssetJumpSection[] }
export const fetchAssetInfo = () => get<AssetInfoConfig>("/asset-info");
export const putAssetInfoApi = (assetType: string, assetId: string, expectedRowVersion: number, info: AssetInfoIn) =>
  send<{ rowVersion: number }>("PUT", `/asset-info/${encodeURIComponent(assetType)}/${encodeURIComponent(assetId)}`, { expected_row_version: expectedRowVersion, info });
/** 自动留痕的 target：这一条的 PUT 请求路径（审计抽屉按它只看这一条） */
export const assetInfoAuditTarget = (assetType: string, assetId: string) => `/admin/api/asset-info/${assetType}/${assetId}`;

// ── 系统邮件（docs/sys-mail-plan.md） ──
//
// 活表、不进配置版本：点发送即生效，没有草稿态。
// 发送必须带稳定的 idem_key——同键同参回放、同键异参冲突，双击不会发出两封都能领的邮件。

export interface MailRow {
  mail_id: string;
  title: string;
  audience: "all" | "user";
  rewards: number;
  recipients: number;
  sent_at_ms: number;
  expire_at_ms: number;
  expired: boolean;
  operator: string;
}

export interface MailRewardIn {
  kind: string;
  ref?: string | null;
  qty: number | null;
}

export interface MailSendIn {
  idem_key: string;
  title_i18n: Record<string, string>;
  /** 所见即所得编辑器产出的 HTML（语言 → HTML）。服务端保存时转成 blocks，HTML 不落库、不上线 */
  body_html_i18n: Record<string, string>;
  rewards: MailRewardIn[];
  audience: "all" | "user";
  user_ids_hex?: string[];
  expire_at_ms: number;
}

/**
 * 正文预览：把编辑器的 HTML 交给**服务端同一个转换函数**，拿回将要落库的 blocks。
 *
 * 管端不自己解析 HTML 画预览——那样预览和真正存进去的会各算各的，
 * 而预览恰恰是用来发现「我写的格式不被支持」的。
 */
export const fetchMailPreview = (html: string) =>
  send<{ blocks: Array<Record<string, unknown>> }>("POST", "/mail/preview", { html });

/** 写邮件弹窗的下拉选项。**按当前生效版本取**——下拉里能选的必须与保存闸认的是同一份 */
export const fetchMailRewardOptions = () =>
  get<{ itemCards: string[]; titles: string[]; actItems: string[] }>("/mail/reward-options");
export const fetchMails = (keyword?: string, limit = 50, offset = 0) =>
  get<{ rows: MailRow[]; total: number }>("/mail", {
    ...(keyword ? { keyword } : {}), limit: String(limit), offset: String(offset),
  });
export const sendMailApi = (mail: MailSendIn) =>
  send<{ mail_id: string; audience: string; recipients: number; replayed: boolean }>("POST", "/mail", mail);
export const withdrawMailApi = (mailId: string) =>
  send<{ mail_id: string; expired: boolean }>("DELETE", `/mail/${encodeURIComponent(mailId)}`);

// ── 举报管理（app/social-report.ts）：列表 + 标记，不做处罚 ──
export type ReportStatus = "open" | "handled" | "ignored";
export interface ReportWho { user_id: string; uid: string; nickname: string | null }
export interface ReportRow {
  report_id: string;
  reporter: ReportWho;
  target: ReportWho;
  reason: string;
  note: string;
  status: ReportStatus;
  handled_by: string;
  handled_note: string;
  created_at_ms: number;
  handled_at_ms: number | null;
}
/** 推送事件（推送管理）：服务端登记表 push/events.ts 的每种推送，按代理开关；活表即时生效 */
export interface PushEventRow {
  key: string; module: string; name: string; description: string;
  text: "template" | "custom";
  text_i18n: { title: Record<string, string>; body: Record<string, string> };
  text_customized: boolean;
  default_enabled: boolean; enabled: boolean;
  updated_at_ms: number | null; updated_by: string | null;
}
export const setPushEventTextApi = (key: string, text: PushEventRow["text_i18n"] | null) =>
  send("PUT", `/push/events/${encodeURIComponent(key)}/text`, { text_i18n: text });
export const fetchPushEvents = () => get<{ events: PushEventRow[] }>("/push/events");
export const setPushEventApi = (key: string, enabled: boolean) =>
  send<{ key: string; enabled: boolean; updated_at_ms: number }>("PUT", `/push/events/${encodeURIComponent(key)}`, { enabled });

export const fetchReports = (opts: { status?: ReportStatus | ""; keyword?: string; limit?: number; offset?: number }) =>
  get<{ rows: ReportRow[]; total: number }>("/reports", {
    ...(opts.status ? { status: opts.status } : {}), ...(opts.keyword ? { keyword: opts.keyword } : {}),
    limit: String(opts.limit ?? 50), offset: String(opts.offset ?? 0),
  });
export const updateReportApi = (reportId: string, status: ReportStatus, note: string) =>
  send<{ updated: true }>("PUT", `/reports/${encodeURIComponent(reportId)}`, { status, note });

// ── 分享链接（admin/share-link.ts）：活表，增 / 改 / 删 / 列表；类型由服务端注册表定 ──
export type ShareLinkType = "social_media" | "creator_center" | "facebook";
export const SHARE_LINK_TYPES: ShareLinkType[] = ["social_media", "creator_center", "facebook"];
export interface ShareLinkIn {
  type: ShareLinkType;
  /** locale → 标题；en 必填（启用时） */
  title_i18n: Record<string, string>;
  url: string;
  /** 走上传管线的 CDN 地址；空串 = 客户端用本地默认图标 */
  icon_url: string;
  sort_order: number;
  enabled: boolean;
}
export interface ShareLinkRow extends ShareLinkIn {
  link_id: string;
  /** = title_i18n.en */
  title: string;
  created_at_ms: number;
  updated_at_ms: number;
}
export const fetchShareLinks = () => get<{ items: ShareLinkRow[] }>("/share-links");
export const createShareLinkApi = (body: ShareLinkIn) => send<{ link_id: string; created_at_ms: number }>("POST", "/share-links", body);
export const updateShareLinkApi = (linkId: string, body: ShareLinkIn) =>
  send<{ updated: true }>("PUT", `/share-links/${encodeURIComponent(linkId)}`, body);
export const deleteShareLinkApi = (linkId: string) => send<{ deleted: true }>("DELETE", `/share-links/${encodeURIComponent(linkId)}`);
// ── 分享设置（admin/share-setting.ts）：文案多语言 + 邀请落地页参数；一代理一行 ──
export interface ShareSettings {
  /** locale → 分享文案模板，占位符 {uid} / {link} */
  share_text_i18n: Record<string, string>;
  /** 客户端的 URL scheme（不带 ://）；空 = 落地页只显示好友码 */
  deep_link_scheme: string;
  ios_store_url: string;
  android_store_url: string;
  invite_ttl_days: number;
  /** 落地页与分享卡片：游戏名、logo（透明底）、竖版背景、分享卡片图（1200×630）；空 = 没配 */
  app_name: string;
  landing_logo_url: string;
  landing_hero_url: string;
  og_image_url: string;
  landing_icon_url: string;
  updated_at_ms: number | null;
}
export const fetchShareSettings = () => get<ShareSettings>("/share-settings");
export const putShareSettingsApi = (body: Omit<ShareSettings, "updated_at_ms">) => send<{ updated: true }>("PUT", "/share-settings", body);
// ── 邀请奖励设置（admin/invite-setting.ts）：达标等级 / 绑定窗口 / 每人奖励 / 满轮大奖；一代理一行 ──
export interface InviteRewardItem { kind: string; ref?: string | null; qty: number | string | null }
export interface InviteSettings {
  /** 被邀请人玩家等级到几级算邀请成功；0 = 绑定就算 */
  min_level: number;
  /** 注册后多少小时内能绑邀请人 */
  bind_window_hours: number;
  per_invite: InviteRewardItem[];
  /** 一轮要几个成功邀请；0 = 不发满轮大奖 */
  round_target: number;
  round_rewards: InviteRewardItem[];
  /** 最多几轮；0 = 不封顶 */
  max_rounds: number;
  updated_at_ms: number | null;
}
export const fetchInviteSettings = () => get<InviteSettings>("/invite-settings");
export const putInviteSettingsApi = (body: Omit<InviteSettings, "updated_at_ms">) => send<{ updated: true }>("PUT", "/invite-settings", body);

// ── 对局监控（只读） ──
//
// 四个 tab 的数据来自三个地方，因为队列和房间**都不落表**：
// 队列 / 房间索引来自大厅 DO，单间房的实况来自房间 DO，已结算 / 未成局才是查库。
// 服务端见 src/admin/match-monitor.ts。

export type {
  MatchLobbyDump, MatchRoomRow, MatchAdminSnapshot, MatchSettledRow, MatchExitRow, MatchReplay,
} from "./matchMonitor";
import type {
  MatchLobbyDump, MatchRoomRow, MatchAdminSnapshot, MatchSettledRow, MatchExitRow, MatchReplay,
} from "./matchMonitor";

export const fetchMatchLobby = () => get<MatchLobbyDump>("/match/lobby");

export const fetchMatchRooms = () =>
  get<{ dump: MatchLobbyDump; rooms: MatchRoomRow[] }>("/match/rooms");

/** 观战：抽屉开着时每 2 秒调一次。房间没了会抛错，界面据此把抽屉关掉 */
export const fetchMatchRoom = (roomId: string) =>
  get<MatchAdminSnapshot>(`/match/rooms/${encodeURIComponent(roomId)}`);

export const fetchMatchResults = (p: {
  mode?: string; endReason?: string; sinceMs?: number; q?: string; page?: number; pageSize?: number;
}) =>
  get<{ rows: MatchSettledRow[]; total: number }>("/match/results", {
    ...(p.mode ? { mode: p.mode } : {}),
    ...(p.q ? { q: p.q } : {}),
    ...(p.endReason ? { end_reason: p.endReason } : {}),
    ...(p.sinceMs ? { since_ms: String(p.sinceMs) } : {}),
    page: String(p.page ?? 1), page_size: String(p.pageSize ?? 20),
  });

export const fetchMatchExits = (p: {
  reason?: string; mode?: string; sinceMs?: number; page?: number; pageSize?: number;
}) =>
  get<{
    rows: MatchExitRow[]; total: number;
    summary: Array<{ reason: string; n: number }>;
    /** 「开局后全员判弃」不在 match_queue_exit 里（那一局真打过），服务端从 match_result 数出来 */
    all_forfeited: number;
  }>("/match/exits", {
    ...(p.reason ? { reason: p.reason } : {}),
    ...(p.mode ? { mode: p.mode } : {}),
    ...(p.sinceMs ? { since_ms: String(p.sinceMs) } : {}),
    page: String(p.page ?? 1), page_size: String(p.pageSize ?? 50),
  });

/**
 * 结算回放：把一局的名次变化压成关键帧。
 *
 * 老局（落过程数据之前打的）和不出回放的占位玩法没有回放，服务端返 **60247**
 * （MATCH_REPLAY_UNAVAILABLE，见 src/domain/errors.ts；60246 是别人的「没有这只宠物」，别写错）。
 * 页面据此显示一行灰字，不当成错误弹红条——那不是故障，是这一局本来就没存。
 */
export const REPLAY_UNAVAILABLE = "60247";

export const fetchMatchReplay = (roomId: string, frames = 8) =>
  get<MatchReplay>(`/match/results/${encodeURIComponent(roomId)}/replay`, { frames: String(frames) });



/** economyModel：当前生效版本的金额口径，成就金币奖励编辑的是哪一份（1 = B 数，2 = 几骰系数） */
export const fetchAchievementProgress = (game: AchievementGame = "ludo") => get<{ buildingSystem: string; maxLevel: number; maxStar: number; economyModel?: 1 | 2 }>("/config/achievement-progress", { game });

/** 活表配置共用当前生效建筑体系，不能跟随正在浏览的历史版本。 */
export const fetchBuildingProgress = () => fetchAchievementProgress();

/* ── 经济口径切换（docs/economy-g-model.md §6，admin/econ-switch.ts）：超管专用，按当前代理 ── */

export type EconPhase = "legacy" | "budget_on" | "switching" | "model2";
/** 一次阶段变化的 journal（服务端 EconJournal，驼峰原样） */
export interface EconJournal {
  switchId: string;
  fromPhase: EconPhase; fromVersion: number;
  toPhase: EconPhase; toVersion: number;
  genAtStart: number;
  status: "preparing" | "committed" | "aborted";
  /** 这一轮要推到所有 DO 的目标围栏 */
  targetGen: number; targetPhase: EconPhase;
  propagation: "pending" | "done";
  startedAtMs: number; finishedAtMs: number | null;
  actor: string;
}
export interface EconStatus {
  active: { version: number; phase: EconPhase; gen: number; switch_id: string | null } | null;
  /** 最近 20 次，新的在前 */
  journals: EconJournal[];
  /** 最近一次的目标围栏：已迁入 DO 的玩家总数 / 已确认数 */
  acks: { total: number; acked: number } | null;
  watchdog: string[];
  /** 最近一次还在 preparing 时的激活闸（不带回放报告与批准人，那两条总在里面） */
  gate: { blockers: string[]; info: Record<string, number> } | null;
  repairs: Array<{ chance_id: string; user_id: string; reason: string; created_at_ms: number }>;
}
export interface EconPropagateResult { journal: EconJournal; dispatched: number; acked: number; total: number; done: boolean; failures: Array<{ user: string; error: string }> }
export interface EconBackfillResult { sql: { done: number; left: number }; do: { done: number; left: number }; failures: Array<{ user: string; error: string }> }

export const fetchEconStatus = () => get<EconStatus>("/econ/status");
export const econBudgetOnApi = () => send<EconJournal>("POST", "/econ/action", { op: "budget_on", deploy1_verified: true });
export const econStartApi = (toVersion: number) => send<EconJournal>("POST", "/econ/action", { op: "start", to_version: toVersion });
export const econPropagateApi = (switchId: string, budget?: number) => send<EconPropagateResult>("POST", "/econ/action", { op: "propagate", switch_id: switchId, budget });
export const econBackfillApi = (switchId: string, budget?: number) => send<EconBackfillResult>("POST", "/econ/action", { op: "backfill", switch_id: switchId, budget });
export const econActivateApi = (switchId: string, replayReport: string, rebalanceApprovedBy: string) =>
  send<EconJournal>("POST", "/econ/action", { op: "activate", switch_id: switchId, replay_report: replayReport, rebalance_approved_by: rebalanceApprovedBy });
export const econAbortApi = (switchId: string) => send<EconJournal>("POST", "/econ/action", { op: "abort", switch_id: switchId });
export const econRestoreApi = (chanceId: string) =>
  send<{ resolved: boolean; source: string | null; reason?: string }>("POST", "/econ/action", { op: "restore", chance_id: chanceId });

/* ── 管端 MCP 个人令牌（admin/mcp-token.ts）：运营自己的 Claude 配卡包用 ── */
export interface McpTokenRow {
  id: string; note: string; created_at_ms: number; expires_at_ms: number;
  last_used_at_ms: number | null; revoked_at_ms: number | null;
}
/** admin = 运营后台 MCP；client = 客户端联调 MCP（游戏 Worker 的 /mcp，只有 test 有） */
export type McpTokenKind = "admin" | "client";
/** enabled=false：这个环境没开这一种 MCP */
export const mcpTokensApi = (kind: McpTokenKind = "admin") =>
  get<{ enabled: boolean; endpoint: string; env?: "test" | "prod"; tokens: McpTokenRow[] }>(`/me/mcp-tokens?kind=${kind}`);
/** 明文 token 只在这一次回来，关掉就看不到了 */
export const createMcpTokenApi = (note: string, kind: McpTokenKind = "admin") =>
  send<{ id: string; token: string; expires_at_ms: number; endpoint: string }>("POST", "/me/mcp-tokens", { note, kind });
export const revokeMcpTokenApi = (id: string) => send<Record<string, never>>("DELETE", `/me/mcp-tokens/${encodeURIComponent(id)}`, {});

/**
 * 二合任务成就（2026-10-29，管端「二合任务成就管理」五个子页：每日任务 / 每日宝箱 / 每周任务 / 成就 / 标签管理）。
 * 与 Idol Ludo 的任务成就**完全分开**：自己的路由 /admin/api/erhe/*（不带 ?game=）、自己的表与校验。
 * 不走配置版本：保存即生效，失败 422 + errors。口径《idol二合-任务与成就配置.xlsx》。
 */
export interface ErheTier { tier: number; target: number; coin: number; ticket: number; gem: number }
export interface ServerErheTask {
  taskId: string;
  section: "daily" | "weekly" | "achievement";
  conditionType: string;
  conditionParam: { min_level?: number; order_type?: string; stage_id?: string } | null;
  category: string;
  pool: "fixed" | "random";
  weight: number;
  activity: number;
  unlockChapter: number;
  titleI18n: Record<string, string>;
  descI18n: Record<string, string>;
  icon: string;
  tiers: ErheTier[];
  sortOrder: number;
  enabled: boolean;
}
export interface ErheChestTier { tier: number; activity: number; coin: number; ticket: number; gem: number }
/** 弹窗底部一个标签的运营配置；服务端返回的是注册表里每个标签一条、已按 sortOrder 排好 */
export interface ErheTabConfig { tabId: string; labelI18n: Record<string, string>; sortOrder: number; enabled: boolean }
/** 保存时不带 tabs = 库里的标签配置不动 */
export interface ErheSettings { dailyRandomCount: number; activityCap: number; chestTiers: ErheChestTier[]; tabs: ErheTabConfig[] }
export interface ErheDict {
  conditions: Array<{ key: string; label: string; source: "internal" | "external"; source_label: string; params: Array<{ key: "min_level" | "order_type" | "stage_id"; label: string }> }>;
  categories: Array<{ key: string; label: string }>;
  sections: Array<{ key: string; label: string }>;
  pools: Array<{ key: string; label: string }>;
  progress_keys: Array<{ key: string; label: string }>;
  /** 标签注册表：label = 默认中文名（运营没填名称时客户端显示它），kind = 客户端画法 */
  tabs: Array<{ key: string; label: string; kind: "daily" | "list" }>;
}
export const fetchErheTasks = () => get<{ tasks: ServerErheTask[]; settings: ErheSettings }>("/erhe/tasks");
export const fetchErheDict = () => get<ErheDict>("/erhe/dict");
export const putErheTaskApi = (task: ServerErheTask, create = false) =>
  send<{ ok: true; warnings?: Array<{ path: string; rule: string; actual: string; expected: string }> }>("PUT", `/erhe/tasks/${encodeURIComponent(task.taskId)}`, { task, create });
export const deleteErheTaskApi = (taskId: string) => send<{ ok: true }>("DELETE", `/erhe/tasks/${encodeURIComponent(taskId)}`, {});
export const putErheSettingsApi = (s: ErheSettings) => send<{ ok: true; settings: ErheSettings }>("PUT", "/erhe/settings", s);
/** 测试面板：把二合某个进度键设成 / 加上指定值（all = 终身、当日、当周都写）/ 清空计数、领取记录与当天抽取 */
export const devErheProgress = (userId: string, op: "set" | "add" | "reset", metric?: string, value?: number, window: "all" | "lifetime" | "daily" | "weekly" = "all") =>
  send<Record<string, unknown>>("POST", "/dev-panel/erhe-progress", { user_id: userId, op, metric, value, window });
